import type { TranslationDictionary } from "../types";

const en: TranslationDictionary = {
  common: {
    english: "English",
    chinese: "中文",
    language: "Language",
    back: "Back",
    cancel: "Cancel",
    copy: "Copy",
    close: "Close",
    delete: "Delete",
    download: "Download",
    edit: "Edit",
    settings: "Settings",
    archived: "Archived",
    retry: "Retry",
    save: "Save",
    send: "Send",
    signOut: "Sign out",
    continue: "Continue",
    restore: "Restore",
    verify: "Verify",
    verifying: "Verifying",
    loading: "Loading...",
    error: "Error:",
    nextChooseSkills: "Next: Choose skills",
    closeNotification: "Close notification",
    closeModal: "Close modal",
  },
  login: {
    title: "The 24/7 Operator for Your Business.",
    subtitle: "Stop working in your business. Start working on it again.",
    signIn: "Sign in or Sign up",
    signingIn: "Signing in",
    signOut: "Sign out",
    prompts: {
      failedOrders: "Fix failed orders in Shopify.",
      attentionToday: "Show me what needs attention today.",
      salesDown: "Explain why sales are down this week.",
      losingConversion: "Find products losing conversion.",
      highValueCustomers: "Identify my highest-value customers.",
      reEngagementCampaign: "Create a re-engagement campaign draft.",
      growStore: "Recommend 3 actions to grow this store.",
      revenueOrders: "Summarize today's revenue and orders.",
      trafficDrops: "Spot unusual traffic drops.",
      winBackCustomers: "Flag customers I should win back.",
    },
    cancelSignIn: "Cancel sign-in",
  },
  sidebar: {
    collapse: "Collapse Sidebar",
    expand: "Expand Sidebar",
    overview: "Overview",
    scheduled: "Scheduled",
    skills: "Skills",
    operations: "Operations",
    addOperation: "Add operation",
    noRecentChats: "No recent chats",
    moreActions: "More actions",
    newTask: "New task",
    archive: "Archive",
    archiveLabel: "Archive {{label}}",
    archiveOperation: "Archive operation",
    archiveOperationTitle: "Archive operation?",
    archiveOperationConfirm:
      "This will archive {{name}}. You can restore it later from Archived.",
    archiveChatsTitle: "Archive {{count}} chat{{plural}}?",
    archiveChatsConfirm: "This will archive the selected chats. You can restore them later from Archived.",
    archiveAll: "Archive all",
    operationSettings: "Operation Settings",
    revealInFinder: "Reveal in Finder",
    revealInExplorer: "Show in Explorer",
    revealInFolder: "Show in Folder",
    workspace: "Workspace",
    memberCount: "{{count}} member{{plural}}",
    inviteMembers: "Invite members",
    allWorkspaces: "All workspaces",
    expired: "EXPIRED",
    createWorkspace: "Create new workspace",
    profile: "Profile",
    general: "General",
    loadingWorkspace: "Loading {{name}}...",
    createOperationTitle: "Add an operation for Bustly",
    chooseSkillsTitle: "Choose skills for this operation",
    operationName: "Operation name",
    chooseIcon: "Choose icon",
    operationDescription: "What should Bustly handle in this operation?",
    operationNamePlaceholder: "e.g. Paid media review",
    operationDescriptionPlaceholder:
      "Describe the tasks, context, and outcomes this operation should handle.",
    deleteOperationTitle: "Delete operation",
    deleteOperationConfirm:
      "Are you sure you want to delete {{name}}? This action cannot be undone.",
    thisOperation: "this operation",
    heartbeat: "Heartbeat",
    pin: "Pin {{label}}",
    unpin: "Unpin {{label}}",
    pinChat: "Pin chat",
    unpinChat: "Unpin chat",
    renameChat: "Rename chat",
    archiveChat: "Archive chat",
    markAsUnread: "Mark as unread",
    copySessionId: "Copy task ID",
    pinOperation: "Pin operation",
    unpinOperation: "Unpin operation",
    pinned: "Pinned",
    archiveAllTasks: "Archive all tasks",
    sortBy: "Sort by",
    sortCreated: "Created",
    sortUpdated: "Updated",
    more: "More",
    moreOperationsActions: "More operations actions",
    collapsePinnedOperations: "Collapse pinned operations",
    expandPinnedOperations: "Expand pinned operations",
    collapseOperations: "Collapse operations",
    expandOperations: "Expand operations",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    showLess: "Show less",
    showMore: "Show more",
    waitingApproval: "Waiting approval",
    selectIcon: "Select icon {{label}}",
  },
  "archivedPage": {
    "title": "Archived",
    "operationsTitle": "Archived Operations",
    "tasksTitle": "Archived Tasks",
    "unknownTime": "Unknown time",
    "restoringOperation": "Restoring...",
    "restoreOperation": "Restore operation",
    "unarchiving": "Unarchiving...",
    "unarchive": "Unarchive",
    "empty": "No archived tasks.",
    "emptyDescription": "Tasks archived from the sidebar will appear here."
  },
  skills: {
    categories: {
      All: "All",
      Recommended: "Recommended",
      General: "General",
      "Ecommerce / DTC": "Ecommerce / DTC",
      "Retail Stores": "Retail Stores",
      "Food & Beverage": "Food & Beverage",
      "Health & Beauty": "Health & Beauty",
      "Professional Services": "Professional Services",
      "Home Services": "Home Services",
    },
    searchForOperation: "Search skills for this operation",
    loadingSkills: "Loading skills...",
    createRecommendationCopy:
      "Based on what this operation needs to handle, we've pre-selected a few recommended skills. You can add more from the skill library.",
    noSkillsFound: "No skills found",
    noMatchingSkills: "No matching skills",
    alreadyEnabled: "All available skills are already added",
    allEnabledDescription:
      "This operation already has every skill from the Hub.",
    addSkillsFromHub: "Add skills from Hub",
    addSkills: "Add skills",
    addSelected: "Add selected",
    addingSkillEnableHint:
      "Added skills are enabled for this operation.",
    noSkillsInHub: "No skills in the Hub yet",
    installOrUploadGlobal:
      "Install or upload skills before adding them to this operation.",
    skillsCount: "{{count}} skills",
    tryAnotherFilter: "Try another keyword or switch the current filter.",
    noSkillsToAdd: "No skills to add",
    noCategorySkillsToAdd: "No {{category}} skills to add",
    noMatchingSkillsToAdd: "No matching skills to add",
    tryAnotherCategory: "Try another category.",
    tryAnotherKeywordOrCategory:
      "Try another keyword or choose a different category.",
    tryAnotherCategoryOrSearch:
      "Try another category or search term.",
    pageTitle: "Skills",
    pageSubtitle:
      "Manage your skill library, and install or update what you need.",
    selectedCount: "Selected ({{count}})",
    clearAll: "Clear All",
    selectAll: "Select All",
    assigning: "Assigning...",
    assignToOperation: "Assign to Operation",
    assignSelected: "Assign selected",
    assignSelectedSkills: "Assign selected skills",
    bulkAssignmentHint:
      "Assigning skills enables them for the selected operations.",
    noOperationsAvailable: "No operations available",
    working: "Working...",
    installSelected: "Install Selected",
    updateSelected: "Update Selected",
    uninstallSelected: "Uninstall Selected",
    manage: "Manage",
    batchInstall: "Batch Install",
    batchUpdate: "Batch Update",
    batchUninstall: "Batch Uninstall",
    upload: "Upload",
    search: "Search skills...",
    installed: "Installed",
    availableToInstall: "Available to Install",
    noSkillsAvailable: "No skills available yet",
    noWorkspaceSkills: "No workspace skills yet",
    noWorkspaceSkillsDescription:
      "Skills saved by people in this workspace will appear here.",
    noSkillsAvailableDescription:
      "Upload one from your machine, import it from GitHub, or build a new skill with Bustly.",
    noSkillsAvailableToInstall: "No skills available to install",
    everythingInstalled:
      "Everything in the current library is already installed.",
    noSkillsNeedUpdate: "No skills need update",
    latestVersion:
      "All installed skills are already on the latest published version.",
    noInstalledSkills: "No installed skills available",
    installFirstManage:
      "Install a skill first, then come back to manage it here.",
    installFirstAssign:
      "Install a skill first, then come back to assign it to an operation.",
    back: "Back",
    saving: "Saving...",
    saveOperation: "Save operation",
    installing: "Installing...",
    downloading: "Downloading...",
    install: "Install",
    download: "Download",
    updating: "Updating",
    update: "Update",
    uninstallSkill: "Uninstall",
    installUnavailable: "Install unavailable",
    viewDetails: "View details",
    capabilities: "Capabilities",
    requiredIntegrations: "Required integrations",
    assignedAgents: "Assigned operations",
    createdBy: "Created by",
    workspaceUser: "Workspace user",
    noAssignedAgents: "Not assigned",
    assignmentEnableHint:
      "Assigning a skill enables it for that operation.",
    noRequiredIntegrations: "No required integrations",
    enabled: "Enabled",
    installedButDisabled: "Installed but disabled",
    status: "Status",
    statusUpdateAvailable: "Update available",
    statusAssigned: "Assigned",
    statusInstalled: "Installed",
    statusNotAdded: "Not added",
    statusUnavailable: "Unavailable",
    actionManageAssignment: "Assign",
    actionAssign: "Assign",
    actionViewReason: "View reason",
  },
  overview: {
    greeting: {
      morning: "Good morning, {{name}}",
      afternoon: "Good afternoon, {{name}}",
      evening: "Good evening, {{name}}",
    },
    "businessHealth": "Business Health",
    "integrations": "Integrations",
    "priority": "Priority",
    "priorityColumn": "Priority",
    "findingSingular": "Priority",
    "findingPlural": "Priority",
    "descriptionColumn": "Description",
    "operationColumn": "Operation",
    "dateColumn": "Date",
    "actionsColumn": "Actions",
    "all": "All",
    "archived": "Archived",
    "healthy": "Healthy",
    "warning": "Warning",
    "critical": "Critical",
    "suggestion": "Suggestion",
    "findingHigh": "High",
    "findingMedium": "Medium",
    "findingSuggestions": "Suggestions",
    "analyzing": "Analyzing...",
    "analyzingDescription": "We're checking how your business is doing.",
    "commandPlaceholder": "Do anything",
    "connectMore": "Connect more. Bustly understands more.",
    "loadingConnections": "Loading connections...",
    "connectionStatusUnavailable": "Connection status unavailable",
    "activeConnections": "{{count}} Active Connection{{plural}}",
    "connectedDataSources": "Connected Data Sources",
    "manageIntegrations": "Manage integrations",
    "justNow": "Just now",
    "notYet": "Not yet",
    "showLess": "Show less",
    "showMore": "Show more",
    "viewDetails": "View details",
    "hideDetails": "Hide details",
    "monitoringOn": "Monitoring On",
    "enableMonitoring": "Turn on",
    "toggleMonitoring": "Toggle monitoring",
    "monitoringOffDescription": "Let Bustly find what needs your attention before you have to look for it.",
    "turnOffMonitoring": "Turn off",
    "gotIt": "Got it",
    "onIt": "On it",
    "takeAction": "Take action",
    "customize": "Customize",
    "customizeHeartbeatForOperation": "Customize what {{operator}} watches",
    "closeHeartbeatCustomize": "Close monitoring goal customization",
    "failedToLoadHeartbeat": "Failed to load monitoring instructions.",
    "failedToSaveHeartbeat": "Failed to save monitoring instructions.",
    "heartbeatInstructionsForOperation": "What should {{operator}} proactively watch for?",
    "heartbeatInstructionsPlaceholderForOperation": "Describe the signals {{operator}} should watch and surface when something needs attention.",
    "heartbeatInstructionsPlaceholders": {
      "marketing": "Example: ad spend rising while orders drop, campaigns performing below target, creative or delivery issues, or broken attribution/data sync.",
      "storeOps": "Example: orders unhandled for 24+ hours, low stock, shipping exceptions, store/payment connection issues, or catalog data problems.",
      "customers": "Example: VIP customer complaints, repeated unanswered messages, rising negative reviews, unusual refund/return requests, or churn risk.",
      "finance": "Example: failed payments, unusual refunds, missing payouts, reconciliation mismatches, chargeback spikes, or abnormal cost increases."
    },
    "allClear": "All clear",
    "nothingNeedsAttention": "Nothing needs your attention at the moment.",
    "nothingToActOn": "Nothing to act on yet",
    "priorityEmptyDescription": "If we spot anything that needs your attention, we'll surface it here.",
    "noArchivedItems": "No archived items yet",
    "archivedEmptyDescription": "Items you've reviewed or acted on will appear here.",
    "untitledIssue": "Untitled issue",
    "unknownOperation": "Unknown operation",
    "newConversation": "New conversation",
    "closeArchiveDrawer": "Close archive drawer",
    "newTask": "New task",
    "openNewTask": "Open new task",
    "closeCommandHub": "Close command hub",
    "tellOperationWhatToDo": "Tell this operation what to do",
    "selectOperation": "Select operation",
    "operations": "Operations",
    "addMenu": "Add files and more",
    "addPhotosAndFiles": "Add photos & files",
    "plugins": "Plugins",
    "chromePlugin": "Chrome",
    "chromePluginDescription": "Use signed-in Chrome tabs for this task",
    "dropFilesToAttach": "Drop files to attach to task",
    "preview": "Preview",
    "failedToLoadIntegrations": "Failed to load workspace integrations.",
    "simulateScan": "Simulate Scan",
    "userOnboardingGuide": {
      "preparedTitle": "Your first operation is ready",
      "preparedDescription": "Bustly set up {{operation}} based on your role, with its prompt and skills already configured.",
      "nextToSettings": "Next 1/2",
      "settingsTitle": "Customize it anytime",
      "settingsDescription": "Open Operation Settings to edit the name, prompt, or skills.",
      "dismiss": "Dismiss onboarding tips",
      "fallbackOperationName": "your operation",
    },
  },
  chatTimeline: {
    toolGroup: {
      exploredFile: "Explored {{count}} file{{plural}}",
      wroteFile: "Wrote file {{count}} time{{plural}}",
      editedFile: "Edited file {{count}} time{{plural}}",
      ranCommand: "Ran {{count}} command{{plural}}",
      managedProcess: "Managed process {{count}} time{{plural}}",
      attachedFile: "Attached file {{count}} time{{plural}}",
      browserTask: "Handled browser task {{count}} time{{plural}}",
      canvasAction: "Controlled canvas {{count}} time{{plural}}",
      nodeAction: "Controlled node {{count}} time{{plural}}",
      sentMessage: "Sent message {{count}} time{{plural}}",
      spokeText: "Spoke text {{count}} time{{plural}}",
      viewedAgentList: "Viewed agent list {{count}} time{{plural}}",
      viewedSessionList: "Viewed task list {{count}} time{{plural}}",
      viewedSessionHistory: "Viewed task history {{count}} time{{plural}}",
      sentSessionMessage: "Sent task update {{count}} time{{plural}}",
      spawnedSession: "Started task {{count}} time{{plural}}",
      managedAgent: "Managed agent {{count}} time{{plural}}",
      viewedSessionStatus: "Viewed task status {{count}} time{{plural}}",
      searchedWeb: "Searched web {{count}} time{{plural}}",
      fetchedWeb: "Fetched web {{count}} time{{plural}}",
      searchedMemory: "Searched memory {{count}} time{{plural}}",
      readMemory: "Read memory {{count}} time{{plural}}",
      managedCron: "Managed scheduled task {{count}} time{{plural}}",
      managedGateway: "Managed gateway {{count}} time{{plural}}",
      whatsappLogin: "Handled WhatsApp login {{count}} time{{plural}}",
      discordAction: "Handled Discord {{count}} time{{plural}}",
      slackAction: "Handled Slack {{count}} time{{plural}}",
      usedTool: "Used {{label}} {{count}} time{{plural}}",
      more: "{{count}} more",
      hiddenActivity: "Hidden activity",
    },
    toolItem: {
      exploredFile: "Explored file",
      wroteFile: "Wrote file",
      editedFile: "Edited file",
      ranCommand: "Ran command",
      managedProcess: "Managed process",
      attachedFile: "Attached file",
      browserTask: "Handled browser task",
      canvasAction: "Controlled canvas",
      nodeAction: "Controlled node",
      sentMessage: "Sent message",
      spokeText: "Spoke text",
      viewedAgentList: "Viewed agent list",
      viewedSessionList: "Viewed task list",
      viewedSessionHistory: "Viewed task history",
      sentSessionMessage: "Sent task update",
      spawnedSession: "Started task",
      managedAgent: "Managed agent",
      viewedSessionStatus: "Viewed task status",
      searchedWeb: "Searched web",
      fetchedWeb: "Fetched web",
      searchedMemory: "Searched memory",
      readMemory: "Read memory",
      managedCron: "Managed scheduled task",
      managedGateway: "Managed gateway",
      whatsappLogin: "Handled WhatsApp login",
      discordAction: "Handled Discord",
      slackAction: "Handled Slack",
      usedTool: "Used {{label}}",
    },
    toolItemRunning: {
      exploredFile: "Exploring file",
      wroteFile: "Writing file",
      editedFile: "Editing file",
      ranCommand: "Running command",
      managedProcess: "Managing process",
      attachedFile: "Attaching file",
      browserTask: "Handling browser task",
      canvasAction: "Controlling canvas",
      nodeAction: "Controlling node",
      sentMessage: "Sending message",
      spokeText: "Speaking text",
      viewedAgentList: "Viewing agent list",
      viewedSessionList: "Viewing task list",
      viewedSessionHistory: "Viewing task history",
      sentSessionMessage: "Sending task update",
      spawnedSession: "Starting task",
      managedAgent: "Managing agent",
      viewedSessionStatus: "Viewing task status",
      searchedWeb: "Searching web",
      fetchedWeb: "Fetching web",
      searchedMemory: "Searching memory",
      readMemory: "Reading memory",
      managedCron: "Managing scheduled task",
      managedGateway: "Managing gateway",
      whatsappLogin: "Handling WhatsApp login",
      discordAction: "Handling Discord",
      slackAction: "Handling Slack",
      usedTool: "Using {{label}}",
    },
    executionFailed: "Execution Failed",
    reason: "Reason",
    retryNow: "Retry Now",
    editMessagePlaceholder: "Edit your request",
    sending: "Sending...",
    openFile: "Open file",
    processed: "Processed",
    processedWithDuration: "Processed in {{duration}}",
    executionCompleted: "Execution Completed",
    copyMessageAria: "Copy request",
    editMessageAria: "Edit request",
  },
  "scheduled": {
    "pageTitle": "Scheduled",
    "pageSubtitle": "Manage recurring tasks and review the latest results in one place.",
    "newScheduled": "New Scheduled",
    "schedulerDisabled": "Scheduled tasks are saved, but they will not run until the gateway cron scheduler is enabled again.",
    "current": "Current",
    "active": "Active",
    "paused": "Paused",
    "loadingTasks": "Loading scheduled tasks...",
    "noActiveTasks": "No active scheduled tasks yet.",
    "noPausedTasks": "No paused scheduled tasks.",
    "unknownTime": "Unknown time",
    "unknownOperation": "Unknown operation",
    "started": "Started",
    "taskInstructions": "Task Instructions",
    "noTaskInstructions": "No task instructions configured.",
    "handledBy": "Handled by",
    "editTask": "Edit task",
    "deleteTask": "Delete task",
    "close": "Close",
    "closeDrawer": "Close drawer",
    "editTitle": "Edit Scheduled Task",
    "newTitle": "New Scheduled Task",
    "addOperationFirst": "Add an operation first. Scheduled tasks only run on operations.",
    "custom": "Custom",
    "createCustomTask": "Create custom task",
    "createCustomTaskDescription": "Start from scratch with your own rules and schedule",
    "templatesCount": "{{count}} templates",
    "taskName": "Task name",
    "taskNamePlaceholder": "e.g. Morning Briefing",
    "taskInstructionsLabel": "Task instructions",
    "taskInstructionsPlaceholder": "e.g. Pull together a concise morning briefing with today's priorities, key updates, and anything that needs my attention",
    "timePlaceholder": "e.g. 09:00 AM",
    "ownerLabel": "Which operation should handle it?",
    "ownerPlaceholder": "Select an operation...",
    "ownerHelp": "Create an operation from the sidebar before scheduling work.",
    "schedule": "Schedule",
    "once": "Once",
    "daily": "Daily",
    "interval": "Interval",
    "at": "at",
    "every": "Every",
    "hours": "hours",
    "minutes": "minutes",
    "days": "days",
    "minimumInterval": "Minimum interval: {{count}} minutes.",
    "selectDate": "Select date",
    "cancel": "Cancel",
    "back": "Back",
    "saving": "Saving...",
    "creating": "Creating...",
    "saveChanges": "Save Changes",
    "createScheduled": "Create Scheduled",
    "chooseOwnerBeforeCreate": "Choose an operation before creating a scheduled task.",
    "failedToBindTasks": "Failed to prepare some scheduled tasks for execution.",
    "failedToLoadTasks": "Failed to load scheduled tasks.",
    "failedToOpenEditor": "Failed to open scheduled task editor.",
    "failedToDeleteTask": "Failed to delete scheduled task.",
    "failedToUpdateTask": "Failed to update scheduled task.",
    "failedToCreateTask": "Failed to create scheduled task.",
    "deleteConfirm": "Delete the scheduled task \"{{name}}\"?",
    "templateGroups": {
      "briefings": "Briefings",
      "marketing": "Marketing",
      "storeOps": "Store Ops",
      "customers": "Customers",
      "finance": "Finance"
    },
    templates: {
      dailyStoreReport: {
        title: "Daily Store Report",
        prompt:
          "Review my store's latest performance across sales, orders, traffic, conversion rate, AOV, repeat purchases, refunds, and ad performance. Compare it with the previous period, highlight the biggest changes, explain what may be causing them, and tell me which issues or opportunities need my attention today.",
      },
      weeklyOpportunityBriefing: {
        title: "Weekly Opportunity Briefing",
        prompt:
          "Review recent sales, traffic, products, ads, customers, repeat purchases, and conversion trends. Highlight the most notable changes, potential opportunities, and issues worth reviewing this week. Suggest practical next steps based on the available data.",
      },
      adSpendWatch: {
        title: "Ad Spend Watch",
        prompt:
          "Review my recent ad spend, ROAS, CPA, CTR, conversion rate, sales contribution, and campaign-level changes. Identify which campaigns or ad sets should keep spending, which need budget adjustments, which may be wasting money, and which should be paused. Explain the reason behind each recommendation.",
      },
      trafficQualityReview: {
        title: "Traffic Quality Review",
        prompt:
          "Review my store's traffic performance, including visitors, sessions, traffic sources, paid traffic, organic traffic, campaign traffic, referral traffic, and returning visitors. Identify major traffic changes, explain what may be driving them, and tell me which channels need attention or optimization.",
      },
      weeklyCampaignReview: {
        title: "Weekly Campaign Review",
        prompt:
          "Review the past week's campaign performance across spend, clicks, traffic quality, conversions, revenue, ROAS, CPA, and audience performance. Summarize what worked, what declined, what should be tested next, and what I should change before the next week starts.",
      },
      orderFollowUpCheck: {
        title: "Order Follow-up Check",
        prompt:
          "Review recent orders and find any that need attention, such as paid orders waiting to ship, fulfillment delays, delivery issues, failed payments, pending orders, or orders that may need manual follow-up. Group the issues by type and tell me which ones should be handled first.",
      },
      inventoryRiskCheck: {
        title: "Inventory Risk Check",
        prompt:
          "Check my inventory for low-stock, out-of-stock, fast-selling, and slow-moving products. Identify items that may hurt sales if not restocked soon, products that are tying up inventory, and any inventory changes that need attention.",
      },
      productPerformanceReview: {
        title: "Product Performance Review",
        prompt:
          "Review product performance and key product pages. Find products with declining sales, low traffic, low conversion, weak add-to-cart rates, poor repeat purchase behavior, or product page issues such as missing images, weak descriptions, unclear pricing, or missing variants. Explain what may be causing the issue and suggest what to improve first.",
      },
      refundCancellationReview: {
        title: "Refund & Cancellation Review",
        prompt:
          "Review recent canceled and refunded orders. Group them by reason, product, channel, customer type, and fulfillment status where possible. Identify where the problems are happening, whether the issue is increasing, and what I should fix first.",
      },
      customerValueReview: {
        title: "Customer Value Review",
        prompt:
          "Review recent customers and identify high-value customers, repeat buyers, inactive customers, first-time buyers with repeat potential, and customers who may need follow-up. Suggest what message or action I should take for each group.",
      },
      payoutRefundCheck: {
        title: "Payout & Refund Check",
        prompt:
          "Review recent payouts, payments, refunds, fees, chargebacks, and unusual payment changes. Compare expected payouts with actual records where possible, flag mismatches or missing payouts, and tell me what needs to be reviewed.",
      },
    },
  },
  agentSettings: {
    fallbackOperation: "Operation",
    identity: "Identity",
    skills: "Skills",
    operationHandles: "What this operation handles",
    save: "Save",
  },
  website: {
    pricing: {
      title: "Pricing for Bustly",
      mostPopular: "Most Popular",
      custom: "Custom",
      perMonth: "/mo",
      processing: "Processing...",
      contactSales: "Contact Sales",
      viewPlans: "View plans",
      contactSalesCta: "Contact Sales →",
      unavailable: "Unavailable",
      availableSoon: "Available soon",
      startSubscription: "Start subscription",
      activatePaidPlan: "Activate Paid Plan",
      switchToSolo: "Switch to Solo",
      switchToTeam: "Switch to Team",
      switchPlan: "Switch plan",
      currentPlan: "Current Plan",
      switchToYearly: "Switch to Yearly",
      switchToMonthly: "Switch to Monthly",
      upgradeToTeam: "Upgrade to Team",
      upgrade: "Upgrade",
      downgrade: "Downgrade",
      downgradeScheduled: "Downgrade scheduled",
      currentPlanActiveUntil:
        "Your current {{plan}} plan remains active until {{date}}.",
      scheduledPlanTakesEffectOn: "{{plan}} will take effect on {{date}}.",
      planWithBillingCycle: "{{plan}} {{cycle}}",
      monthly: "Monthly",
      yearly: "Yearly",
      groupWorkspaceWorkUnits: "Workspace & Work Units",
      groupTrainingIntegrations: "Support & integrations",
      teamExclusiveBadge: "Team Exclusive",
      comparePlansTitle: "Compare plans & features",
      comparisonFeature: "Key limits",
      comparisonWorkspace: "Workspace",
      comparisonWorkspaceUsers: "Workspace users",
      comparisonStores: "Store connections",
      comparisonConnections: "Integrations",
      comparisonSlackAndFeishu: "Slack & Feishu",
      comparisonOtherIntegrations: "Other integration connections",
      comparisonTotalConnections: "Total integration connections",
      comparisonWorkUnits: "Work Units & tasks",
      comparisonMonthlyWorkUnits: "AI Operator Work Units per month",
      comparisonEstimatedTasks: "Tasks handled per month (estimated)",
      comparisonSupported: "Supported",
      comparisonNotSupported: "Not supported",
      notIncluded: "Not included",
      monthlyLower: "monthly",
      yearlyLower: "yearly",
      tenPercentOff: "10% OFF",
      cancelSubscription: "Cancel subscription",
      resubscribe: "Resubscribe",
      editBilling: "Edit billing",
      soloName: "Solo",
      teamName: "Team",
      team1499Name: "Team (10 users)",
      teamFiftyUsersName: "Team (50 users)",
      frontendTierPlanName: "{{plan}} ({{value}} Work Units)",
      team1499UserLabel: "10 users",
      teamUnlimitedUserLabel: "50 users",
      scaleName: "Scale",
      soloDescription:
        "For founders and solo operators hiring their first AI operator.",
      teamDescription:
        "For companies rolling out AI operators across the workspace.",
      scaleDescription:
        "For larger organizations rolling out AI operators at scale.",
      soloFeatureOne: "1 workspace user included",
      soloFeatureStoreLimit: "1 store connection included",
      soloFeatureThree: "Available 24/7",
      soloFeatureFour:
        "9,780 AI Operator Work Units per month for lighter daily operations",
      creditTopUps: "On-demand Work Unit top-ups",
      teamFeatureOneStarter: "10 workspace users included",
      teamFeatureOne: "50 workspace users included",
      teamFeatureThree: "Available 24/7",
      teamFeatureFour: "Up to 10 store connections included",
      teamFeatureFiveStarter:
        "20,980 shared AI Operator Work Units per month for growing team operations",
      teamFeatureFive:
        "69,980 shared AI Operator Work Units per month for higher team task volume",
      selectSoloTier: "Select Solo option",
      selectTeamTier: "Select Team option",
      workUnitsPerMonth: "{{value}} Work Units / month",
      workUnitsToBeConfirmed: "Work Units to be confirmed",
      oneUserShort: "1 user",
      usersShort: "{{value}} users",
      oneStoreShort: "1 store",
      storesShort: "{{value}} stores",
      unlimitedUsersShort: "Unlimited users",
      unlimitedStoresShort: "Unlimited stores",
      customStoresShort: "Custom stores",
      unlimited: "Unlimited",
      customStoreConnectionsIncluded: "Custom store connections",
      userLimitShortTbd: "User limit TBD",
      storeLimitShortTbd: "Store limit TBD",
      workspaceUsersIncluded: "{{value}} workspace users included",
      storeConnectionsIncluded: "Up to {{value}} store connections included",
      unlimitedWorkspaceUsersIncluded: "Unlimited workspace users included",
      unlimitedStoreConnectionsIncluded: "Unlimited store connections included",
      workspaceUserLimitTbd: "Workspace user limit to be confirmed",
      storeConnectionLimitTbd: "Store connection limit to be confirmed",
      workUnitsFeature:
        "{{value}} AI Operator Work Units per month for lighter daily operations",
      sharedWorkUnitsFeature:
        "{{value}} shared AI Operator Work Units per month for higher team task volume",
      workUnitsFeatureTbd: "Monthly AI Operator Work Units to be confirmed",
      sharedWorkUnitsFeatureTbd:
        "Monthly shared AI Operator Work Units to be confirmed",
      builtInAiCapabilities: "Built-in AI capabilities",
      createImagesWith: "Create images with {{model}}",
      createVideosWith: "Create videos with {{model}}",
      handleBusinessTasksWith: "Handle business tasks with {{model}}",
      includedIntegrations: "Included integrations",
      estimatedSoloOperationsPerMonth:
        "Handles about {{min}}–{{max}} routine operational tasks per month",
      estimatedTeamOperationsPerMonth:
        "Handles about {{min}}–{{max}} team operational tasks per month",
      estimatedTaskCountTooltip:
        "Actual task counts depend on task complexity, data volume, and how often Bustly reads data from connected apps or performs write actions in them.",
      recordedCoursesIncluded:
        "On-demand video courses to help you get started with Bustly",
      weeklyLiveSessionsIncluded:
        "Weekly live sessions on practical ways to use Bustly",
      onsiteTrainingIncluded:
        "Onsite training to help your team deploy and implement Bustly, valued at $2,000",
      estimatedScaleOperations:
        "Estimated capacity tailored to your workflows",
      previewTierUnavailableDescription:
        "This option is not available for purchase yet. Choose another option to continue.",
      configuredTierUnavailableDescription:
        "This option is not available for purchase yet. Choose another option to continue.",
      scaleFeatureOne: "Unlimited workspace users included",
      scaleFeatureTwo: "Unlimited store connections included",
      scaleFeatureThree: "Flexible deployment and support",
      scaleFeatureFour: "Custom monthly AI Operator Work Units",
      scaleFeatureFive: "Tailored workflow design",
      noWorkspaceSelected: "No workspace selected",
      planNotFound: "Plan not found",
      failedToStartPaidCheckout: "Failed to start paid checkout",
      failedToStartCheckout: "Failed to start checkout",
      failedToOpenBillingPortal: "Failed to open billing portal",
      activatePaidPlanConfirmTitle: "Activate {{plan}}?",
      activatePaidPlanConfirmDescriptionPrefix:
        "You are about to activate the paid",
      activatePaidPlanConfirmDescriptionMiddle: "plan on a {{cycle}}",
      activatePaidPlanConfirmDescriptionSuffix:
        "billing cycle. Your trial will end now and the paid subscription will take effect immediately.",
      upgradeToPlanTitle: "Upgrade to {{plan}}",
      upgradeToPlanDescriptionPrefix: "You are about to upgrade to the",
      upgradeToPlanDescriptionMiddle: "plan on a {{cycle}}",
      upgradeToPlanDescriptionSuffix:
        "billing cycle. The upgrade will take effect immediately, and you will be charged a prorated amount for the remainder of your current billing cycle.",
      downgradeToPlanTitle: "Downgrade to {{plan}}",
      downgradeToPlanDescriptionPrefix: "You are about to downgrade to the",
      downgradeToPlanDescriptionSuffix:
        "plan. This change will take effect at the end of your current billing cycle. You will keep your current features until then.",
      switchBillingTitle: "Switch to {{cycle}} Billing",
      switchBillingDescription:
        "Switching to {{cycle}} billing will take effect at the next renewal. You will continue to have access to your current plan until the end of your current {{currentCycle}} cycle.",
      cancelSubscriptionTitle: "Cancel subscription?",
      cancelSubscriptionPendingPaymentDescription:
        "If you cancel now, Shopify will charge only for days used and apply a credit or refund for unused time.",
      cancelSubscriptionActivePaidDescription:
        "After you confirm, auto-renewal will turn off. This subscription is not refunded. Your plan access and Plan Work Units stay active until this billing cycle ends.",
      confirmCancelSubscription: "Cancel subscription",
      resubscribeTitle: "Resubscribe?",
      resubscribeDescription:
        "You'll go to Shopify to approve a new subscription for this plan.",
      confirmResubscribe: "Resubscribe",
      subscriptionPendingPaymentCanceledToast: "Subscription canceled.",
      subscriptionActivePaidCanceledToast:
        "Subscription canceled. Current plan benefits stay active until the cycle ends.",
      failedToCancelSubscription:
        "We couldn't cancel your subscription. Please try again.",
      openingShopifySubscriptionApprovalToast:
        "Opening Shopify to approve your subscription.",
      renewSubscription: "Renew Subscription",
      renewSubscriptionDescriptionPrefix:
        "You are about to renew your current subscription. This will resume",
      autoRenewal: "auto-renewal",
      renewSubscriptionDescriptionSuffix: "for your current plan.",
      confirmChange: "Confirm Change",
      confirmRenewal: "Confirm Renewal",
      manage: "Manage",
      trialPlanUpdatedToast:
        "Plan updated. You can activate the paid plan now, or keep the trial until it ends.",
      planChangeScheduledToast:
        "Plan change scheduled. It will take effect at your next renewal.",
      planUpgradedToast: "Plan upgraded successfully.",
      subscriptionRenewedToast: "Subscription renewed successfully.",
      subscriptionCreatedToast: "Subscription created successfully.",
      failedToRenewSubscription: "Failed to renew subscription.",
      failedToCreateSubscription: "Failed to create subscription.",
    },
    welcomeModal: {
      nextPulse: {
        title: "Don’t miss tomorrow’s Pulse",
        description:
          "Upgrade your Bustly plan to receive a daily Pulse at 7:00 AM. Your first free Pulse remains available.",
        later: "Maybe later",
        upgrade: "Upgrade",
        imageAlt: "Welcome",
      },
      workspaceReady: {
        checkingTitle: "Preparing your cloud workspace...",
        readyTitle: "Workspace is ready",
        checkingDescription:
          "We are starting your cloud agent and connecting it to your workspace. This usually takes a few minutes.",
        adminPageDescription:
          "This page is for workspace settings, teammates, integrations, and billing.",
        desktopName: "Bustly Desktop",
        desktopDescriptionSuffix:
          " is where you start tasks and review the work.",
        imageAlt: "Workspace is ready",
        caption: "Open Bustly Desktop to start your first task.",
        waitingInstance: "Waiting for your instance to become ready...",
        openDesktop: "Open Bustly Desktop",
        downloadQuestion: "Don’t have Bustly Desktop?",
        downloadApp: "Download the app",
      },
      inviteSuccess: {
        title: "You’re in",
        caption: "Download Bustly Desktop to start your first task.",
        downloadDesktop: "Download Bustly Desktop",
        alreadyHaveDesktop: "Already have Bustly Desktop?",
        openApp: "Open the app",
      },
    },
    paymentSuccess: {
      title: "Payment successful",
      subscriptionSummary: "This workspace is now subscribed to Bustly.",
      subscriptionSummaryWithPlan:
        "This workspace is now subscribed to Bustly {{plan}}.",
      subscriptionLineOpenDesktop: "Start tasks in Bustly Desktop",
      subscriptionLineUsePlan: "Use this workspace plan for daily operations",
      subscriptionLineManageBilling: "Manage billing from Billing & Plans",
      teamLineInvite: "Invite teammates to this workspace",
      teamLineConnect: "Connect multiple stores, accounts, and apps",
      teamLineManageRoles: "Manage roles and access controls",
      soloLineOpenDesktop: "Start tasks in Bustly Desktop",
      soloLineUseCredits:
        "Use monthly AI Operator Work Units for daily operations",
      soloLineBuyCredits: "Buy extra Work Units when needed",
      creditsDescription:
        "Your purchased Work Units have been added to this workspace and are ready to use.",
      close: "Close payment success",
      openDesktop: "Open Bustly Desktop",
      downloadQuestion: "Don’t have Bustly Desktop?",
      downloadApp: "Download the app",
    },
    waiting: {
      paymentReturn: {
        title: "Finalizing your payment...",
        message:
          "Updating your plan and billing details. This usually takes a few seconds.",
        errorMessage: "Payment is still processing",
        continueAction: "Continue",
      },
      desktopOAuth: {
        title: "Signing in to Bustly...",
        message:
          "Finishing the secure handoff to Bustly Desktop. This usually takes a few seconds.",
        retryAction: "Retry",
        missingContext:
          "The Bustly Desktop sign-in handoff is missing. Please restart login from Bustly Desktop.",
        missingWorkspace:
          "The workspace for this sign-in could not be found. Please restart login from Bustly Desktop.",
        missingCallbackUrl:
          "Bustly Desktop did not return the sign-in handoff. Please try again from the desktop app.",
        invalidCallbackUrl:
          "The sign-in handoff could not be verified. Please try again from Bustly Desktop.",
      },
    },
    pricingContactSales: {
      title: "Talk to Sales",
      description: "Get a custom demo and pricing for your specific needs.",
      successTitle: "Thank you!",
      successSubtitle: "Your request has been sent.",
      successMessage:
        "Our team will review your information and get back to you within 24 hours to schedule a personalized demo.",
      close: "Close",
      firstName: "First Name",
      firstNamePlaceholder: "First name",
      lastName: "Last Name",
      lastNamePlaceholder: "Last name",
      workEmail: "Work Email",
      companyName: "Company Name",
      companyNamePlaceholder: "Your company",
      jobTitle: "Job Title",
      jobTitlePlaceholder: "e.g. CEO, Marketing Director",
      companySize: "Company Size",
      companySizePlaceholder: "Select size",
      companySizeOptions: {
        oneToTen: "1-10 employees",
        elevenToFifty: "11-50 employees",
        fiftyOneToTwoHundred: "51-200 employees",
        twoHundredOneToFiveHundred: "201-500 employees",
        fiveHundredPlus: "501+ employees",
      },
      brandCount: "Number of Brands/Stores",
      brandCountPlaceholder: "Select count",
      primaryUseCase: "Primary Use Case",
      primaryUseCasePlaceholder: "Select use case",
      primaryUseCaseOptions: {
        marketAnalysis: "Market Analysis & Insights",
        contentCreation: "Content Creation",
        productResearch: "Product Research",
        competitorTracking: "Competitor Tracking",
        other: "Other",
      },
      needs: "Anything else we should know?",
      needsPlaceholder: "Tell us about your specific needs...",
      submitRequest: "Submit Request",
      legalText:
        "By submitting this form, you agree to our Terms of Service and Privacy Policy.",
      submitFailed: "Failed to send request. Please try again later.",
      errors: {
        firstNameRequired: "First name is required",
        lastNameRequired: "Last name is required",
        emailRequired: "Email is required",
        emailInvalid: "Please enter a valid email address",
        companyNameRequired: "Company name is required",
        companySizeRequired: "Please select company size",
        brandCountRequired: "Please select number of brands/stores",
        primaryUseCaseRequired: "Please select a primary use case",
      },
    },
    connectShopify: {
      title: "Bring Bustly into the store.",
      subtitle:
        "Pick the platform and share the storefront URL so Bustly can learn the business.",
      connect: "Connect",
      checking: "Checking...",
      storeUrlLabel: "Shopify Store URL",
      storeUrlPlaceholder: "Your store URL",
      storeUrlTooltip:
        "Login to your Shopify and navigate down to Domains section.",
      findUrlTitle: "Find your myShopify URL",
      findUrlDescriptionPrefix: "In Shopify, go to",
      findUrlDescriptionMiddle: ", then navigate to",
      domains: "Domains",
      stepLabel: "Step {{count}}",
      screenshotPlaceholder: "Screenshot placeholder",
      closeImagePreview: "Close image preview",
      helpStepOneAlt: "Shopify settings page showing where to open Domains",
      helpStepTwoAlt:
        "Shopify domains page showing the myShopify URL location",
      devStates: "Dev states:",
      defaultRemainingTrialText: "3 days",
      validation: {
        alreadyBound: {
          title: "Connection failed.",
          body:
            "This store is already bound to another workspace. A store can only be connected to one workspace at a time.",
        },
        trialValid: {
          title: "Existing trial detected.",
          body:
            "This store already has an active trial. After connecting to this workspace, it will continue with the remaining {{remainingTrialText}} only.",
        },
        trialExpired: {
          title: "Trial already used.",
          body:
            "This store has already consumed its free trial. Trial time cannot be reissued across workspaces. Continue to billing to connect this store with a paid plan.",
        },
      },
      connectFailedRetry: "Connect to Shopify failed. Please try again.",
      validStoreNameRequired: "Please enter a valid Shopify store name",
      failedToConnectStore:
        "Failed to connect to Shopify store. Please try again.",
      invalidStoreUrl: "Invalid Shopify store URL",
      storeNameRequired: "Please enter your Shopify store name",
      validStoreUrlRequired:
        "Please enter a valid Shopify store URL (e.g., yourstore.myshopify.com)",
      storeNameTooShort: "Store name must be at least 3 characters long",
      storeNameInvalidChars:
        "Store name can only contain letters, numbers, and hyphens",
      storeNameHyphenBoundary: "Store name cannot start or end with a hyphen",
    },
    integrationConnectGuides: {
      referenceImageComingSoon: "Reference image coming soon",
      placeholderImageAlt: "Connection guide reference image",
      shopify: {
        title: "Find your myShopify URL",
        description:
          "In Shopify, go to Settings, then open Domains to find the myShopify URL for this store.",
      },
      bigCommerce: {
        title: "Prepare your BigCommerce details",
        description:
          "Have your Store Hash and Account UUID ready before you connect.",
        steps: {
          openControlPanel: {
            title: "Open BigCommerce",
            description: "Open the control panel and confirm the store you want to connect.",
          },
          findStoreHash: {
            title: "Find the Store Hash",
            description: "Copy the Store Hash from the store URL or API Path.",
          },
          findAccountUuid: {
            title: "Find the Account UUID",
            description: "Copy the Account UUID from the account-level API account or API Path.",
          },
        },
      },
      feishu: {
        title: "Prepare your Feishu custom app",
        description:
          "Have your App ID, App Secret, and app permissions ready before you connect.",
        steps: {
          openPlatform: {
            title: "Open Feishu Open Platform",
            description: "Create or open the custom app you want Bustly to use.",
            imageAlt: "Feishu Open Platform custom app list with create buttons highlighted.",
          },
          copyCredentials: {
            title: "Copy app credentials",
            description: "Copy the App ID and App Secret from Credentials & Basic Info.",
            imageAlt: "Feishu app created screen showing App ID and App Secret fields.",
          },
          addPermissions: {
            title: "Add app permissions",
            description: "Add the permissions this workspace needs, then publish or approve the app if required.",
            imageAlt: "Feishu Permissions and Scopes page showing added permission scopes.",
          },
        },
      },
      ahrefs: {
        title: "Prepare your Ahrefs API key",
        description:
          "Create or copy an API key so Bustly can sync Ahrefs data.",
        steps: {
          openApiKeys: {
            title: "Open API keys",
            description: "Go to Ahrefs Account settings, then open API keys.",
            imageAlt: "Ahrefs account settings API keys page.",
          },
          createKey: {
            title: "Create a key",
            description: "Create a new API key. Workspace owners and admins can manage keys.",
            imageAlt: "Ahrefs Generate API key dialog with key title input.",
          },
          pasteKey: {
            title: "Paste the key into Bustly",
            description: "Add a clear label so teammates can recognize this connection.",
            imageAlt: "Ahrefs generated API key dialog with copy button.",
          },
        },
      },
      omnisend: {
        title: "Prepare your Omnisend API key",
        description:
          "Create an API key with the data permissions this workspace needs.",
        steps: {
          openApiKeys: {
            title: "Open API keys",
            description: "Go to Omnisend Store Settings, then open API keys.",
            imageAlt: "Omnisend Store settings API keys page.",
          },
          createKey: {
            title: "Create a key",
            description: "Create a new API key and name it for Bustly.",
            imageAlt: "Omnisend Create API key permissions screen.",
          },
          copyKey: {
            title: "Copy the key",
            description: "Copy the key before closing the screen, then paste it into Bustly.",
            imageAlt: "Omnisend Copy API key screen with copy button.",
          },
        },
      },
      okendo: {
        title: "Prepare your Okendo API credentials",
        description:
          "Create or copy your Okendo User ID and API Key from Settings > Integrations > Credentials, then paste each value into its matching Bustly field.",
        steps: {
          openCredentials: {
            title: "Open integration credentials",
            description: "In Okendo, go to Settings, Integrations, then Credentials.",
            imageAlt: "Okendo Integration Settings page with Integrations selected and the edit button highlighted.",
          },
          copyUserIdAndApiKey: {
            title: "Copy User ID and API Key",
            description: "Select the Okendo app you want Bustly to use, then copy both the User ID and API Key.",
            imageAlt: "Okendo Credentials panel showing User ID and Merchant REST API Key copy controls.",
          },
          pasteCredentials: {
            title: "Paste into Bustly",
            description: "Paste the User ID and Merchant REST API Key into their separate fields, then add a label teammates can recognize.",
            imageAlt: "Bustly Okendo connection dialog showing separate User ID and Merchant REST API Key fields.",
          },
        },
      },
      prestashop: {
        title: "Prepare your PrestaShop Webservice key",
        description:
          "Enable Webservice in PrestaShop, create a key, and grant the resource permissions this workspace needs.",
        steps: {
          openWebservice: {
            title: "Open Webservice settings",
            description: "In PrestaShop, open Advanced Parameters, then Webservice.",
            imageAlt: "PrestaShop Webservice settings page with Advanced Parameters and Add new webservice key highlighted.",
          },
          createKey: {
            title: "Create a Webservice key",
            description: "Add a new key, enable it, choose the resource permissions, and save.",
            imageAlt: "PrestaShop New webservice key page with generated key, permissions, and Save button.",
          },
          copyKey: {
            title: "Copy the key into Bustly",
            description: "Confirm Webservice is enabled, copy the key, then paste it into Bustly.",
            imageAlt: "PrestaShop Webservice list showing an enabled Bustly Integration key.",
          },
        },
      },
      figma: {
        title: "Prepare your Figma personal access token",
        description:
          "Create a Figma personal access token with the file and library permissions this workspace needs.",
        steps: {
          openSettings: {
            title: "Open Figma settings",
            description: "In Figma, open Account settings, then go to Security.",
          },
          createToken: {
            title: "Create a token",
            description: "Create a personal access token and select the REST API scopes this workspace needs.",
          },
          pasteToken: {
            title: "Paste the token into Bustly",
            description: "Copy the token before closing Figma, then paste it here with a recognizable label.",
          },
        },
      },
    },
    workspaceSwitcher: {
      basic: "Basic",
      manage: "Manage",
      expired: "Expired",
      trial: "Trial",
      startSubscription: "Start subscription",
      upgrade: "Upgrade",
      selectWorkspace: "Select Workspace",
    },
    userMenu: {
      login: "Login",
      homepage: "Homepage",
      user: "User",
    },
    profileSettings: {
      failedToUpdatePassword: "Failed to update password",
      updatePassword: "Update password",
      newPassword: "New Password",
      enterNewPassword: "Enter new password",
      hidePassword: "Hide password",
      showPassword: "Show password",
      passwordUpdatedSuccessfully: "Password updated successfully!",
      updating: "Updating...",
      confirm: "Confirm",
      selectImageFile: "Please select an image file",
      imageSizeTooLarge: "Image size must be less than 5MB",
      uploadFailed: "Upload failed",
      failedToUploadAvatar: "Failed to upload avatar",
      profileAlt: "Profile",
      uploading: "Uploading...",
      change: "Change",
      name: "Name",
      email: "Email",
      password: "Password",
    },
    settingsModal: {
      account: "Account",
      general: "General",
      generalDescription: "General settings and preferences",
      language: "Language",
      languageDescription: "Language for this interface",
      workspace: "Workspace",
      workspaceRecord: "Workspace Record",
      profileDescription: "Personal information and security",
      workspaceSettings: "Workspace Settings",
      workspaceSettingsDescription: "Workspace details and preferences",
      workspaceSkills: "Workspace Skills",
      workspaceSkillsDescription: "Review reusable ways of working learned from completed tasks",
      integrations: "Integrations",
      integrationsDescription: "Connect your tools to Bustly",
      users: "Users",
      usersDescription: "Manage team access",
      valueLedger: "Outcome Summary",
      valueLedgerDescription: "Review workspace outcome summaries and value",
      audit: "Audit log",
      auditDescription: "Review cloud Agent Run events",
      billingAndPlans: "Billing & Plans",
      billingDescription: "Manage subscription and invoices",
      usage: "Usage",
      usageDescription: "Monitor Work Units and task usage",
      downloads: "Downloads",
      downloadsDescription: "Download Bustly Desktop",
      saving: "Saving...",
      saveChanges: "Save Changes",
      comingSoon: "Coming Soon",
      reusableSkillSuggestions: "Reusable skill suggestions",
      reusableSkillSuggestionsDescription:
        "When on, Bustly helps you capture reusable skills after each task. When off, no new skill suggestions are shown.",
    },
    workspaceSkills: {
      subtitle: "Workspace Skills save useful methods from completed tasks so your team can use them again.",
      loading: "Loading workspace skills...",
      unavailable: "Workspace skills are unavailable",
      loadFailed: "Failed to load workspace skills.",
      retry: "Retry",
      emptyTitle: "Workspace Skills will appear here",
      emptyDescription: "New Workspace Skills are created after completed tasks are reviewed and approved.",
      pagination: "Workspace Skills pages",
      previousPage: "Previous",
      nextPage: "Next",
      pageNumber: "Page {{page}}",
      showingSkillsRange: "Showing {{start}} to {{end}} of {{total}} skills",
      createdBy: "Created by",
      unknownCreator: "Workspace member",
      updatedAt: "Last updated:",
      viewSkillDetail: "View details",
      skillDetailTitle: "Skill details",
      closeSkillDetail: "Close skill details",
      loadingSkillDetail: "Loading skill details...",
      detailLoadFailed: "Failed to load skill details.",
      deleteSkill: "Remove skill",
      deleteSkillTitle: "Remove skill from this workspace?",
      deleteSkillDescription: "{{name}} will be removed from this workspace. Its saved versions will be kept.",
      deleteSkillConfirm: "Remove",
      deleteSkillSuccess: "Skill removed from this workspace.",
      deleteSkillFailed: "Failed to remove skill from this workspace.",
      guideTitle: "Turn completed tasks into reusable skills",
      guideDescription:
        "Bustly captures useful methods from completed tasks and saves them as Workspace Skills your team can use again.",
      turnOn: "Turn on Workspace Skills",
      ownerOnlyHint: "Only the Workspace Owner can turn on Workspace Skills.",
      creationNote: "New Workspace Skills are created only after completed tasks are reviewed and approved.",
      previewCompletedTask: "Completed task",
      previewReviewed: "Reviewed",
      previewReusableSkill: "Reusable skill",
      previewReady: "Ready for your team",
      pausedDescription:
        "Workspace Skills is paused. Existing skills remain available, but Bustly won't create or update skills from newly completed tasks.",
      pauseAction: "Pause",
      resumeAction: "Turn on",
      resumeLabel: "Turn on Workspace Skills",
      pauseTitle: "Pause Workspace Skills?",
      pauseDescription:
        "Bustly will stop creating or updating Workspace Skills from newly completed tasks. Existing skills will remain available.",
      pauseConfirm: "Pause",
      settingsUpdateFailed: "We couldn't update Workspace Skills. Try again.",
    },
    valueLedger: {
      workspaceFallback: "this workspace",
      ownerView: "Owner view",
      title: "Outcome Summary",
      subtitle:
        "Review the traceable outcomes Bustly produced for {{workspaceName}}.",
      lastUpdated: "Last updated {{date}}",
      waitingForData: "Waiting for outcome data",
      waitingForFirstRefresh: "Preparing Outcome Summary",
      outcomeSummaryTag: "Daily outcome preview",
      outcomeSummaryTitle: "Review Bustly's key outcomes",
      outcomeSummaryDescription:
        "See what workspace users and Bustly moved forward each day, which outcomes have evidence, and which workflows are worth keeping.",
      outcomeSummaryGetStarted: "Turn on",
      outcomeSummaryScheduleDescription:
        "Daily summaries run at {{time}}. You can turn them off anytime.",
      outcomeSummaryPreviewTitle: "After you turn it on, you'll see",
      outcomeSummaryAdvancedWorkTitle: "Work that moved forward",
      outcomeSummaryAdvancedWorkDescription:
        "Results are broken down by checks, execution, delivery, and validation, not just task counts.",
      outcomeSummaryTraceableEvidenceTitle: "Traceable evidence",
      outcomeSummaryTraceableEvidenceDescription:
        "Keeps the checked objects, actions taken, validation results, and risk notes.",
      outcomeSummaryRepeatableWorkflowTitle: "Repeatable workflows",
      outcomeSummaryRepeatableWorkflowDescription:
        "Surfaces recurring decision rules, exception handling, and handoff patterns.",
      outcomeSummaryFinePrintTitle: "Note: Daily summaries use workspace Work Units.",
      outcomeSummaryFinePrintDescription:
        "Bustly reads completed Tasks and related context in this workspace only to generate this page. That data is not used for other purposes. Bustly runs on AWS and Google Cloud infrastructure, with mature security and privacy controls that protect data while it is processed.",
      outcomeSummaryToggleLabel: "Toggle Outcome Summary",
      outcomeSummaryPermissionHint:
        "Only users with permission to manage workspace settings can change this.",
      outcomeSummaryExpiredHint:
        "Workspace subscription has expired. Outcome Summary is stopped and historical content remains visible.",
      updateCadence: "Outcome Summary refreshes daily at {{time}} ({{timezone}})",
      firstRefreshPendingTitle: "Generating the outcome view",
      firstRefreshPendingDescription:
        "Reading recent workspace tasks and preparing verifiable outcome cards.",
      startingOutcomeSummaryTitle: "Generating the outcome view",
      startingOutcomeSummaryDescription:
        "Reading recent workspace tasks and preparing verifiable outcome cards.",
      preparingOutcomeSummaryTitle: "Generating the outcome view",
      preparingOutcomeSummaryDescription:
        "Reading recent workspace tasks and preparing verifiable outcome cards.",
      preparingOutcomeSummaryStepReadTasks: "Reading completed workspace tasks",
      preparingOutcomeSummaryStepExtractEvidence: "Extracting sub-outcomes and evidence notes",
      preparingOutcomeSummaryStepGroupWorkflows: "Grouping reusable workflows",
      dailyUpdates: "Daily updates",
      dailyUpdatesOff: "Daily updates paused",
      historicalDataNotice:
        "Showing historical data. Turn on daily updates to refresh it.",
      disableDialogTitle: "Pause daily updates?",
      disableDialogDescription:
        "Historical Outcome Summary content will stay visible. Bustly will stop daily refreshes and won't use Work Units for Outcome Summary until you turn them on again.",
      disableAction: "Pause",
      disableDialogConfirm: "Pause",
      resumeAction: "Turn on",
      resumeDailyUpdatesLabel: "Turn on daily updates",
      permissionDeniedTitle: "Permission Denied",
      permissionDeniedDescription:
        "You do not have permission to view Outcome Summary details.",
      settingsUpdateFailed:
        "We couldn't update Outcome Summary settings. Try again in a moment.",
      statRange: "Subscription cycle",
      currentCycle: "Current subscription cycle",
      currentCycleDescription: "Tasks and outcomes in this subscription cycle.",
      previousCycle: "Previous subscription cycle",
      previousCycleDescription: "Compare against the previous subscription cycle.",
      recentThreeCycles: "Recent 3 subscription cycles",
      recentThreeCyclesDescription: "Review the latest operating pattern.",
      customCycle: "Custom range",
      customCycleDescription: "Choose a specific date range.",
      billingCycle: "Subscription cycle",
      memberTasks: "User-started tasks",
      memberTasksDescription: "Tasks submitted by workspace users.",
      bustlyOutcomes: "Bustly outcomes",
      bustlyOutcomesDescription: "Classified work results ready for review.",
      overviewEyebrow: "Current subscription cycle",
      overviewHeadline: "Bustly handled core {{system}} operations this cycle",
      overviewHeadlineFallback: "Bustly handled core operations this cycle",
      overviewDescription:
        "The team started {{taskCount}} Tasks, with {{userCount}} users involved. {{coreTaskCount}} Tasks focused on {{taskArea}}, making up {{share}} of all user-started Tasks.",
      overviewUserStartedTasksMetric: "User-started Tasks",
      overviewActiveUsersMetric: "Users involved",
      overviewTopAreaTasksMetric: "Tasks in top area",
      overviewTopAreaShareMetric: "Share of user-started Tasks",
      overviewValueJudgmentTitle: "Core value assessment",
      overviewTaskAreasTitle: "Top Task areas this cycle",
      overviewTaskAreasDescription:
        "Sorted by user-started Tasks in this subscription cycle.",
      overviewTaskAreaColumn: "Task area",
      overviewTaskCountColumn: "Tasks",
      overviewUserCountColumn: "Users",
      overviewRepresentativeWorkColumn: "Representative work",
      overviewInsightsTitle: "Efficiency gains Bustly has already created",
      overviewInsightsDescription:
        "These gains come from real Tasks, not general product claims.",
      overviewStandardWorkflowTitle: "First workflow to standardize",
      overviewReviewSuggestionTitle: "Review the outcome ledger each week, not only usage",
      overviewChampionsTitle: "Champion users already running repeat Tasks",
      overviewChampionsDescription:
        "These users are using Bustly repeatedly in real work, not just testing it.",
      overviewNextCycleTitle: "Pick 3 goals for the next cycle",
      overviewNextCycleDescription:
        "Avoid broad training. Turn proven, frequent Tasks into a team workflow.",
      estimatedTimeSaved: "Estimated time saved",
      estimatedTimeSavedDescription:
        "Calculated after task duration rules are connected.",
      estimatedValue: "Outcome estimate",
      estimatedValueDescription:
        "Calculated after estimate rules are connected.",
      handoffEyebrow: "Outcome handoff",
      handoffTitle: "Outcome handoff",
      handoffDescription:
        "Each outcome will show its scope, evidence, source task, and review basis.",
      byOutcome: "By outcome",
      byTask: "By task",
      outcomeInbox: "Outcome inbox",
      outcomeRecords: "Outcome records",
      outcomesInRange: "{{shown}} / {{total}} outcomes in this subscription cycle",
      searchOutcomes: "Search outcomes or source tasks",
      clearSearch: "Clear search",
      clearFilters: "Clear filters",
      paginationRange: "Showing {{start}} to {{end}} of {{total}} outcomes",
      paginationPage: "Page {{current}} of {{total}}",
      previousPage: "Previous page",
      nextPage: "Next page",
      jumpToPage: "Go to page",
      pageNumber: "Page number",
      goToPage: "Go",
      noMatchingOutcomes: "No matching outcomes.",
      noMatchingTasks: "No matching tasks.",
      detailTabOverview: "Outcome",
      detailTabEvidence: "Proof",
      outcomeType: "Outcome type",
      businessModule: "Business area",
      allOutcomeTypes: "All outcome types",
      allBusinessAreas: "All business areas",
      executionOutcomes: "Execution outcomes",
      executionOutcomesDescription:
        "Operational changes completed in connected tools.",
      judgmentOutcomes: "Judgment outcomes",
      judgmentOutcomesDescription:
        "Decisions, checks, or comparisons Bustly prepared.",
      stewardshipOutcomes: "Stewardship outcomes",
      stewardshipOutcomesDescription:
        "Monitoring, follow-up, and ongoing watch work.",
      blockerOutcomes: "Blocker resolution",
      blockerOutcomesDescription: "Work that moved a stalled task forward.",
      adsModule: "Ads and acquisition",
      productModule: "Product, inventory, merchandising",
      customerModule: "Customer, membership, CRM",
      contentModule: "Content, SEO, page assets",
      emptyTitle: "Workspace outcomes will show up here.",
      emptyDescription:
        "After Bustly classifies tasks, each outcome includes its scope, evidence, and source task.",
      scopeField: "Scope",
      evidenceField: "Evidence",
      sourceTaskField: "Source task",
      businessScope: "Scope",
      evidenceSummary: "Evidence summary",
      systemsEvidence: "Integrations",
      sourceTask: "Source task",
      taskType: "Task type",
      taskStatus: "Task status",
      acceptanceStatus: "Acceptance status",
      memberShort: "User",
      messagesShort: "Messages",
      executionShort: "Execution",
      outcomesShort: "Outcomes",
      taskOutcomes: "Task outcomes",
      traceId: "Source",
      bustlyCost: "Recorded processing cost",
      tokensProcessed: "Processing volume",
      missingInputs: "Missing inputs",
      executionActionCount: "{{count}} actions",
      collaborationRecordCount: "{{count}} collaboration records",
      viewOutcomeDetail: "View outcome details",
      outcomeDetail: "Outcome details",
      outcomeDetailPosition: "Item {{current}} of {{total}}",
      closeOutcomeDetail: "Close outcome details",
      backToList: "Back to list",
      detailLoadError: "Detail failed to load. Showing the list summary.",
      outcomeInformation: "Outcome description",
      concreteOutput: "What Bustly delivered",
      memberNextStep: "User next step",
      bustlyProcessingJudgment: "Bustly judgment",
      classificationBasis: "Why this counts",
      establishmentConditions: "Evidence and next step",
      billingAcceptanceBasis: "Acceptance basis",
      assistantMessages: "Bustly replies",
      billingEvents: "Supporting records",
      collaborationRecords: "Collaboration records",
      systems: "Systems",
      riskNextStep: "Risk and next step",
      nonBillableAndDispute: "Outcome inclusion and review basis",
      nonBillableCheck: "Inclusion check",
      disputeBasis: "Review basis",
      traceRecord: "Source record",
      taskOverview: "Task overview",
      updatedAt: "Updated",
      traceability: "Evidence path",
      traceabilityTitle: "Evidence path",
      memberMessages: "User messages",
      memberMessagesDescription: "Original requests and follow-up context.",
      systemExecution: "System execution",
      systemExecutionDescription: "Actions, tools, and data sources used.",
      traceConversation: "Source conversation",
      traceConversationLoading: "Loading full source conversation...",
      traceConversationLoaded: "{{count}} user/Bustly messages",
      traceConversationExecutionCollapsed: "{{count}} execution records collapsed",
      traceConversationMissing:
        "No source conversation is available for this snapshot.",
      traceConversationError: "Source conversation could not be loaded.",
      laborOutcomes: "Outcomes",
      laborOutcomesDescription: "Results Bustly can hand off.",
      acceptanceBasis: "Acceptance basis",
      acceptanceBasisDescription: "Evidence for reviewing the outcome.",
      valueModel: "Outcome estimate",
      valueModelTitle: "Savings basis",
      timeCost: "Time cost",
      timeCostDescription: "Estimated human effort for similar tasks.",
      laborCost: "Labor cost",
      laborCostDescription: "Workspace labor rate used for outcome estimates.",
      economicValue: "Outcome estimate",
      economicValueDescription:
        "Calculated estimate after time and labor rules are available.",
    },
    auditLog: {
      title: "Audit log",
      subtitle: "Review cloud Agent Run events for this workspace.",
      eventCount: "{{count}} events",
      executionCount: "{{count}} executions",
      loading: "Loading cloud audit events...",
      workspaceUnavailable: "Choose a workspace to review its cloud audit events.",
      forbidden: "You don't have permission to view the audit log.",
      loadError: "We couldn't load cloud audit events. Try again.",
      tryAgain: "Try again",
      empty: "Audit logs will appear here as activity is recorded.",
      emptyTitle: "No audit logs yet",
      noMatches: "Try changing or clearing your filters.",
      noMatchesTitle: "No matching audit logs",
      clearFilters: "Clear filters",
      searchPlaceholder: "Search audit logs, apps, or members",
      allApplications: "All apps",
      allInitiators: "All members",
      today: "Today",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      allTime: "All time",
      allPlatforms: "All platforms",
      allMembers: "All members",
      allStatuses: "All statuses",
      allOperations: "All operations",
      filterByApplication: "Filter by app",
      filterByInitiator: "Filter by member",
      filterByTime: "Filter by time",
      filterSelectedCount: "{{count}} selected",
      pagination: "Audit log pages",
      previousPage: "Previous",
      nextPage: "Next",
      pageNumber: "Page {{page}}",
      showingPageCount: "Showing {{count}} audit log{{plural}}",
      showSystemEvents: "Show system events",
      systemEventsHidden: "{{count}} system events are hidden. Turn on Show system events to review them.",
      platform: "Platform",
      member: "Member",
      initiator: "Member",
      involvedApps: "Apps",
      pendingLocation: "Pending",
      sourceTask: "Source task",
      appReadWrite: "App reads/writes",
      readWriteCount: "Read {{read}} · Write {{write}}",
      keyActions: "Key actions",
      timeline: "Timeline",
      originalConversation: "Original conversation",
      originalConversationWithCount: "Original conversation {{count}}",
      viewOriginalConversation: "View conversation",
      action: "Action",
      readApplication: "Read app",
      writeApplication: "Write app",
      noBusinessEvents: "This execution has no business events to display.",
      conversationEmptyTitle: "Original conversation not recorded",
      conversationEmptyBody: "This run does not include original conversation content.",
      conversationUnavailable: "Original conversation content is not available.",
      conversationLoading: "Loading original conversation...",
      conversationForbidden: "You don't have permission to view the original conversation.",
      conversationError: "We couldn't load the original conversation.",
      userRole: "User",
      toolRole: "Tool",
      connection: "Connection",
      resource: "Resource",
      operation: "Operation",
      status: "Status",
      changedFields: "Changed fields",
      viewContentDetails: "View content details",
      contentDetails: "Content details",
      contentLoading: "Loading content details...",
      contentUnavailable: "Content is not available for this event.",
      contentNotCaptured: "No content was captured for this event.",
      contentRedacted: "Content is unavailable because it was redacted.",
      contentDeleted: "Content is no longer available.",
      contentUnknown: "Content availability is not available for this event.",
      contentForbidden: "You don't have permission to view content details.",
      contentError: "We couldn't load content details.",
      close: "Close",
      closeAuditDetails: "Close audit details",
      viewDetails: "View details",
      permissionDeniedTitle: "No access to audit log",
      loadFailedTitle: "Audit logs couldn't load",
      eventTimeline: "Cloud event details",
      noEventSelected: "Select an event to review its facts.",
      executionTime: "Execution time",
    },
    sidebar: {
      welcome: "Welcome",
    },
    clientAuth: {
      logOut: "Log out",
      returnToClient: "Please Return to Bustly Desktop to Continue",
      openBustly: "Open Bustly",
      openBustlyAria: "Open Bustly",
      clientMockupAlt: "Bustly Desktop Mockup",
      mismatchTitle: "Accounts Mismatch",
      mismatchDescription:
        "The website is currently using {{account}}, which does not match the account active in Bustly Desktop. Switch to the same account to keep your workflow in sync.",
      switchToClientAccount: "Switch to Bustly Desktop account",
      continueWithWebsiteAccount: "Continue with the website account",
      successTitle: "You have successfully logged in!",
      redirectPrompt: "Not automatically redirected?",
      prepareDesktopHandoffFailed: "Failed to prepare the desktop handoff.",
    },
    logoutDialog: {
      title: "Log out?",
      signedInAs: "You're signed in as {{account}}.",
      accountFallback: "your account",
      confirm: "Log out",
      cancel: "Stay signed in",
    },
    auth: {
      continueWithGoogle: "Continue with Google",
      or: "Or",
      emailPlaceholder: "Enter your email address",
      welcomeBack: "Welcome back",
      continueWithVerificationCode: "Continue with verification code",
      continueWithPassword: "Continue with password",
      verifyEmailAddress: "Verify your email address",
      verifyEmailDescription:
        "Enter the code sent to {{email}} to complete verification.",
      verificationCodePlaceholder: "Enter verification code",
      resend: "Resend",
      signInWithPasswordInstead: "Sign in with password instead",
      enterPassword: "Enter your password",
      email: "Email",
      emailAddress: "Email address",
      editEmail: "Edit",
      password: "Password",
      forgotPassword: "Forgot password?",
      passwordPlaceholder: "Enter password",
      signInWithVerificationCodeInstead:
        "Sign in with verification code instead",
      createAccount: "Create your account",
      createPassword: "Create your password",
      setPasswordToContinue: "Set your password to continue",
      minPasswordPlaceholder: "Min 8 characters",
      resetPassword: "Reset your password",
      resetPasswordDescription:
        "We will send a 6-digit verification code to the following address to verify your account.",
      resetPasswordButton: "Reset password",
      sendingEmailCode: "Sending email code...",
      resetPasswordCodeDescription:
        "We have sent a verification code to {{email}}.",
      verificationCode: "Verification code",
      newPassword: "New password",
      newPasswordPlaceholder: "Enter new password",
      confirmPassword: "Confirm Password",
      confirmNewPasswordPlaceholder: "Confirm new password",
      updatePassword: "Update password",
      resetUpdatingPassword: "Updating password...",
      legalPrefix: "By continuing, you agree to the",
      legalAnd: "and",
      termsOfService: "Terms of service",
      privacyPolicy: "Privacy policy",
      showPassword: "Show password",
      hidePassword: "Hide password",
      passwordSetSuccessfully: "Password set successfully!",
      accountSetupComplete: "Account setup complete!",
      verificationCodeResent: "Verification code resent!",
      connectError: "An error occurred during connection",
      connectStore: "Connect Store",
      connecting: "Connecting...",
      errors: {
        incorrectVerificationCode: "Incorrect verification code",
        expiredVerificationCode: "Expired verification code",
        passwordIncorrect: "That password doesn't look right.",
        verifyEmailFirst:
          "Please check your inbox and verify your email first.",
        emailAlreadyUsed:
          "This email is already in use. Try signing in instead.",
        passwordTooWeak: "That password is too weak. Try something stronger.",
        passwordReused:
          "Use a new password that you have not used for Bustly before.",
        checkAndTryAgain:
          "Something doesn't look right. Please check and try again.",
        slowDown: "Whoa, slow down! Give it a moment and try again.",
        serverMoment:
          "Our servers are having a moment. Please try again later.",
        signUpUnavailable: "Signing up is currently unavailable.",
        connectionTrouble:
          "Having trouble connecting. Check your internet and try again.",
      },
      validation: {
        enterEmail: "Please enter your email address",
        enterValidEmail: "Please enter a valid email address",
        enterPassword: "Please enter your password",
        passwordMin: "Password should be at least 8 characters",
        passwordNoOuterSpaces: "Password cannot start or end with a space.",
        passwordCommon:
          "That password is too common. Try a more unique password.",
        passwordSimpleSequence: "Avoid simple sequences like 123456 or qwerty.",
        passwordContainsEmail: "Password cannot include the email address.",
        passwordContainsPersonalInfo:
          "Password cannot include obvious account or product information.",
        enterSixDigitCode: "Please enter the 6-digit verification code.",
        enterNewPassword: "Please enter your new password.",
        passwordsDoNotMatch: "Passwords do not match.",
      },
    },
    authError: {
      otpExpired: {
        title: "Link Expired",
        description:
          "The password reset link has expired. Please request a new one.",
      },
      accessDenied: {
        title: "Access Denied",
        description: "You do not have permission to access this resource.",
      },
      emailNotConfirmed: {
        title: "Email Not Verified",
        description: "Please verify your email address before continuing.",
        action: "Resend Verification Email",
      },
      invalidCredentials: {
        title: "Invalid Credentials",
        description: "The email or password you entered is incorrect.",
        action: "Try Again",
      },
      shopifyDevelopmentStoreBlocked: {
        title: "Development Store Not Supported",
        description:
          "Bustly can only connect to live Shopify stores. Please connect a real merchant store instead.",
        toast:
          "Development stores are not supported. Please connect a live Shopify merchant store.",
      },
      default: {
        title: "An Error Occurred",
        description: "Something went wrong. Please try again later.",
        action: "Go Back",
      },
      closingConnectionWindow: "Closing connection window",
      backToSignIn: "Back to Sign In",
    },
    ownerTransferAccept: {
      missingToken:
        "This ownership transfer link is missing required information.",
      invalidOrExpired: "This ownership transfer link is invalid or expired.",
      acceptedRefreshFailed:
        "Ownership was accepted, but we could not refresh your workspace list.",
      acceptFailed: "Failed to accept ownership transfer. Please try again.",
      anotherAccount: "another account",
      acceptedTitle: "Ownership accepted",
      expiredTitle: "Ownership transfer link expired",
      cancelledTitle: "Ownership transfer cancelled",
      completedTitle: "Ownership transfer completed",
      unavailableTitle: "Ownership transfer unavailable",
      acceptTitle: "Accept ownership of {{workspace}}",
      thisWorkspace: "this workspace",
      expiredDescription:
        "This ownership transfer link has expired. Ask the current owner to send a new transfer invitation.",
      cancelledDescription:
        "This ownership transfer invitation was cancelled by the current owner. Ask them to send a new invitation if you still need access.",
      completedDescription: "This ownership transfer has already been accepted.",
      unavailableDescription:
        "This ownership transfer is no longer available. Ask the current owner to send a new invitation if needed.",
      signingOut: "Signing out...",
      loggingOut: "Logging out...",
      signOutAndSwitch: "Sign out and switch account",
      logOut: "Log out",
      currentOwnerFallback: "The current owner",
      invitedDescription:
        "{{owner}} invited you to become the owner of {{workspace}}.",
      loading: "Loading transfer request...",
      differentAccountDescription:
        "This transfer request is for {{email}}. Sign in with that account to accept it.",
      newOwner: "New owner",
      pendingUser: "Pending user",
      signInToContinue: "Sign in to continue",
      accepting: "Accepting...",
      acceptOwnership: "Accept ownership",
    },
    workspaceSettings: {
      ownershipTransferredTitle: "Ownership transferred",
      ownershipTransferred:
        "{{name}} is now the Owner. Your role is now {{role}}.",
      ownerTransferRequestSentTitle: "Confirmation email sent",
      ownerTransferRequestSent:
        "{{name}} will become Owner after accepting the confirmation email.",
      ownerTransferPendingTitle: "Ownership transfer pending",
      ownerTransferPendingDescription:
        "Waiting for {{name}} to accept the ownership transfer email.",
      ownerTransferSentAt: "Sent at {{date}}",
      ownerTransferExpiresAt: "Expires at {{date}}",
      cancelOwnershipTransfer: "Cancel transfer",
      cancellingOwnershipTransfer: "Cancelling...",
      ownershipTransferCancelled: "Ownership transfer request cancelled.",
      ownerTransferNoLongerPending:
        "This ownership transfer request has expired or is no longer pending. The page has been refreshed.",
      transferFailedTitle: "Transfer failed",
      transferFailed: "Transfer failed: {{reason}}",
      transferFailedFallback: "Failed to transfer workspace ownership.",
      workspaceLogo: "Workspace Logo",
      uploading: "Uploading...",
      uploadLogo: "Upload Logo",
      workspaceName: "Workspace Name",
      industry: "Industry",
      selectIndustry: "Select your industry...",
      specifyIndustry: "Please specify your industry",
      specifyIndustryPlaceholder: "e.g. Manufacturing, Real Estate, etc.",
      industries: {
        ecommerce: {
          label: "E-commerce",
          description: "Operate your store with more clarity and control",
        },
        retail: {
          label: "Retail",
          description: "Keep stores, teams, and operations moving together",
        },
        restaurants: {
          label: "Restaurants",
          description: "Stay on top of service, demand, and daily operations",
        },
        fitness: {
          label: "Fitness",
          description: "Run your business with more consistency and momentum",
        },
        beauty: {
          label: "Beauty",
          description: "Keep appointments, clients, and growth on track",
        },
        agencies: {
          label: "Agencies",
          description: "Bring more structure to delivery and client operations",
        },
        "home-services": {
          label: "Home Services",
          description: "Keep jobs, teams, and service running smoothly",
        },
        automotive: {
          label: "Automotive",
          description: "Run a more efficient and responsive service operation",
        },
        events: {
          label: "Events",
          description: "Keep planning, coordination, and execution aligned",
        },
        education: {
          label: "Education",
          description: "Support day-to-day operations with more consistency",
        },
        other: {
          label: "Other",
          description: "Shape Bustly around how your business operates",
        },
      },
      saving: "Saving...",
      saveChanges: "Save Changes",
      transferOwnership: "Transfer workspace ownership",
      transferOwnershipDescription:
        "Send an ownership transfer confirmation email. The new owner only takes over after they open the link and accept.",
      transfer: "Transfer",
      selectNewOwner: "Select new owner",
      selectUser: "Select a user...",
      searchUsers: "Search users...",
      noUsersFound: "No users found",
      ownershipTransferPreviewPrefix: "Once transferred, ",
      selectedTransferUserFallback: "the selected user",
      ownershipTransferPreviewMiddle:
        " will become the new Workspace Owner with full control over billing, settings, and users. You will remain in this workspace as an ",
      ownershipTransferPreviewSuffix: ".",
    },
    admin: {
      unsavedChangesTitle: "Unsaved Changes",
      unsavedChangesDescription:
        "You have unsaved changes. Are you sure you want to leave? Your changes will be lost.",
      discardAndLeave: "Discard & Leave",
    },
    subscriptionExpired: {
      illustrationAlt: "Bustly renewal illustration",
      warningIllustrationAlt: "Billing warning illustration",
      title: "Keep Bustly running",
      description: "Choose a plan to continue with Bustly.",
      viewPlans: "View plans",
      expiredDialogTitle: "Your subscription has expired",
      cancelledDialogTitle: "Your subscription has been canceled",
      dialogDescription: "Update your payment details to get back on track.",
      updatePaymentInfo: "Update Payment Info",
    },
    membersSettings: {
      allRoles: "All roles",
      filterRoles: "Filter roles...",
      noRolesFound: "No roles found",
      clear: "Clear",
      clearUserSearch: "Clear user search",
      noRolesAvailable: "No roles available",
      selectRole: "Select role",
      newRole: "New Role",
      removeUser: "Remove user",
      remove: "Remove",
      accountsTab: "Account",
      rolesTab: "Roles",
      searchUsers: "Search users...",
      addNewUser: "Add new user",
      user: "User",
      role: "Role",
      status: "Status",
      joinedDate: "Joined Date",
      actions: "Actions",
      noUsersMatching: 'No users found matching "{{query}}"',
      showingUsersRange: "Showing {{start}} to {{end}} of {{total}} users",
      pagination: "User pages",
      previousPage: "Previous page",
      nextPage: "Next page",
      pageNumberLabel: "Page {{page}}",
      emailAddress: "Email Address",
      inviteEmailPlaceholder: "colleague@company.com",
      resendInvite: "Resend",
      resendingInvite: "Resending...",
      invitationResent: "Invitation resent",
      invitationResentDescription:
        "We sent a new invitation email to {{email}}. They can join “{{workspace}}” by clicking “Join the Workspace” in the email.",
      invitationSent: "Invitation sent",
      invitationSentDescription:
        "We sent an invitation email to {{email}}. They can join “{{workspace}}” by clicking “Join the Workspace” in the email.",
      invitationFailed: "Invitation Failed",
      failedToInviteUser: "Failed to invite user.",
      statusPending: "Pending",
      statusActive: "Active",
      emailRequired: "Email is required",
      emailInvalid: "Please enter a valid email address",
      emailAlreadyMember: "This email is already a member.",
      selectedRoleNoLongerAvailable:
        "Selected role is no longer available.",
      selectedAccessRoleInvalid: "Selected access role is invalid.",
      invitationResendFailed: "Resend failed",
      failedToResendInvite: "Failed to resend invitation.",
      cancelInvite: "Cancel",
      cancellingInvite: "Cancelling...",
      invitationCancelled: "Invitation cancelled",
      invitationCancelledDescription:
        "The pending invitation for {{email}} has been cancelled.",
      invitationCancelFailed: "Cancel failed",
      failedToCancelInvite: "Failed to cancel invitation.",
      removeUserConfirmation: "Are you sure you want to remove",
      removeUserIrreversible: "? This action cannot be undone.",
      integrationOwnerRemovalWarning:
        "This user manages access for {{count}} connection{{plural}}. Access management will transfer to the Workspace Owner.",
      newOwner: "New owner",
      thisUser: "This user",
      transferOwnershipDescription:
        "will receive a confirmation email. They become the new owner only after accepting it. You'll remain in this workspace as an",
      transferring: "Sending...",
      transferOwnership: "Send confirmation",
      userRemoved: "User Removed",
      userRemovedDescription: "The user has been removed from the workspace.",
      removalFailed: "Removal Failed",
      failedToRemoveUser: "Failed to remove user.",
      workspaceUnavailable: "Workspace is not available.",
      selectedRoleUnavailable:
        "Selected role is not available for this workspace.",
      updateFailed: "Update Failed",
      cannotChangeOwnRole: "You cannot change your own role.",
      roleUpdated: "Role Updated",
      roleUpdatedDescription: "{{role}} role has been updated successfully.",
      failedToUpdateMemberRole: "Failed to update member role.",
      usersReassigned: "Users Reassigned",
      usersReassignedDescription:
        "Updated {{count}} user{{plural}} to {{role}}.",
      reassignmentFailed: "Reassignment Failed",
      failedToUpdateMembers: "Failed to update one or more members.",
      rolePermissionsUnavailable:
        "Workspace role permissions are not available.",
      roleSaved: "Role Saved",
      roleSavedDescription: "Role permissions have been saved successfully.",
      saveFailed: "Save Failed",
      failedToSaveRolePermissions: "Failed to save role permissions.",
      roleDeleted: "Role Deleted",
      roleDeletedDescription: "The role has been deleted successfully.",
      deleteFailed: "Delete Failed",
      failedToDeleteRole: "Failed to delete role.",
      roleAccessUnavailable: "Role access unavailable",
      failedToLoadNewRoleAccess:
        "Failed to load connection access for the new role.",
      couldNotResolveCurrentOwner: "Could not resolve current workspace owner.",
      unableToLoadUsers: "Unable to load users",
      tryAgainInMoment: "Try again in a moment.",
      retry: "Retry",
      upgradeRequiredTitle: "You need to upgrade",
      inviteUpgradeDescription:
        "You’re currently on Solo. Upgrade to Team to add users to this workspace.",
      upgradeToInviteMembersTitle: "Upgrade to invite members",
      soloInviteLimitDescription:
        "Your Solo plan supports 1 workspace user. Choose a Team plan to invite members.",
      rolesUpgradeDescription:
        "You’re currently on Solo. Upgrade to Team to create and manage workspace roles.",
      viewTeamPlan: "View Team plan",
      workspaceUserLimitReachedTitle: "Workspace user limit reached",
      workspaceUserLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}} workspace users. Choose a plan with a higher user limit to invite more members.",
      workspaceUserLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}} workspace users. Contact Sales if you need more workspace users.",
      workspaceUserLimitPlansFallbackDescription:
        "This workspace has reached its user limit. Choose a plan with a higher user limit to invite more members.",
      workspaceUserLimitSalesFallbackDescription:
        "Your workspace has reached its user limit. Contact Sales if you need more workspace users.",
      pluginWorkspaceUserLimitDescription:
        "Your Plugin plan supports up to {{limit}} workspace user. This Plugin workspace does not have self-serve upgrades.",
      ownerRole: "Owner",
      adminRole: "Admin",
      billingRole: "Billing",
      memberRole: "Member",
      ecommerceOpsRole: "Ecommerce Operations",
      paidMediaRole: "Paid Media & Channel Growth",
      seoContentRole: "SEO & Content Growth",
      socialInfluencerRole: "Social & Influencer Ops",
      crmLifecycleRole: "CRM & Lifecycle Marketing",
      businessInsightsRole: "Business Analytics & Insights",
      siteExperienceRole: "Site Experience & QA",
      editRole: "Edit role",
      roleName: "Role name",
      roleNamePlaceholder: "e.g. Marketing, Finance",
      roleNameExists:
        "A role with this name already exists. Choose a different name.",
      workspacePermissions: "Workspace permissions",
      manageWorkspaceSettings: "Manage workspace settings",
      manageWorkspaceSettingsDescription:
        "Users with this role can update how this workspace is set up.",
      manageUsers: "Manage users",
      manageUsersDescription:
        "Users with this role can manage who has access to this workspace.",
      manageRolesAndAccess: "Manage roles and access",
      manageRolesAndAccessDescription:
        "Users with this role can decide what each role can access and do.",
      manageIntegrations: "Manage integrations",
      manageIntegrationsDescription:
        "Users with this role can connect and disconnect integrations.",
      manageConnectionAccess: "Manage connection access",
      manageConnectionAccessDescription:
        "Users with this role can set each user's access to connected accounts: No access, View only, or View & take action.",
      manageBilling: "Manage billing",
      manageBillingDescription:
        "Users with this role can manage subscriptions, payment methods, invoices, and Work Unit purchases.",
      viewUsage: "View usage",
      viewUsageDescription:
        "Users with this role can see how Work Units are being used in this workspace.",
      viewOutcomeSummary: "View Outcome Summary",
      viewOutcomeSummaryDescription:
        "Users with this role can view Outcome Summary for this workspace.",
      manageOutcomeSummary: "Manage Outcome Summary",
      manageOutcomeSummaryDescription:
        "Users with this role can turn daily updates on or off and refresh Outcome Summary.",
      manageWorkspaceSkills: "Manage workspace skills",
      manageWorkspaceSkillsDescription:
        "Users with this role can view and delete skills created by workspace members.",
      viewAuditLog: "View audit log",
      viewAuditLogDescription:
        "Users with this role can view this workspace's audit log, original conversations, and content details.",
      connectionAccess: "Connection access",
      createRole: "Create Role",
      saveRoleChangesTitle: "Save role changes?",
      saveRoleChangesWithUsers:
        "This role is currently assigned to {{count}} user{{plural}}. Saving will update permissions for everyone using this role.",
      saveRoleChangesDescription:
        "Saving will update this role everywhere it is used going forward.",
      assignedTo: "{{count}} user{{plural}}",
      editRoleAria: "Edit {{role}} role",
      deleteRole: "Delete role",
      deleteRoleAria: "Delete {{role}} role",
      deleteRoleDescription:
        'Are you sure you want to delete the role "{{role}}"?',
      manageRolesPermissionDenied:
        "You do not have permission to manage roles and access.",
      rolesIntro:
        "Use roles to decide what users can manage and which connections they can use.",
      noRolesYet: "No roles yet",
      noRolesYetDescription:
        "Create a role to define tailored access for your team.",
      reassignUsersBeforeDeleting: "Reassign users before deleting",
      reassignRoleBeforeDeleteDescription:
        'Role "{{role}}" is assigned to {{count}} user{{plural}}. Choose a new role and we\'ll reassign them, then delete this role in one step.',
      newRoleForTheseUsers: "New role for these users",
      noReplacementRoleAvailable:
        "No replacement role available for these users.",
      usersUsingThisRole: "Users using this role",
      reassigning: "Reassigning...",
      reassignAndDeleteRole: "Reassign & Delete Role",
    },
    onboarding: {
      common: {
        back: "Back",
        skip: "Skip",
        continue: "Continue",
        close: "Close",
        logOut: "Log out",
      },
      runtime: {
        provisioning: "Provisioning cloud environment...",
        configuring: "Configuring your workspace...",
        preparing: "Preparing always-on execution...",
        finalizing: "Finalizing your cloud setup...",
        inlineTitle: "Just a moment, and Bustly will be ready to work.",
        fullscreenTitle: "Your Bustly cloud workspace is being set up",
        fullscreenSubtitle: "Just a moment, it'll be ready soon.",
        setupProgress: "Setup Progress",
      },
      failure: {
        title: "We are still checking your workspace",
        description:
          "Your payment or workspace activation is still being confirmed. Please try again in a moment.",
        returnToOnboarding: "Return to onboarding",
        goToAdmin: "Go to Admin",
      },
      notice: {
        dismiss: "Dismiss repeated store notice",
        freeTrialUnavailableTitle: "Free trial unavailable",
        freeTrialUnavailableDescription:
          "This store is already connected to another workspace or has used a Bustly free trial, so it can't start a new free trial here.\n\nConnect another store, or make sure 4 apps are connected before choosing a paid plan.",
        freeTrialUnavailableWorkspaceFallback: "Another workspace",
        freeTrialUnavailableCancel: "Connect another store",
        freeTrialUnavailableContinue: "Choose a paid plan",
        freeTrialUnavailableContinueSetup: "Continue setup",
        storeLimitReachedTitle: "Store connection limit reached",
        storeLimitReachedDescription:
          "Your current plan has reached its store connection limit. Upgrade or contact Sales to connect more stores.",
      },
      invite: {
        thisWorkspace: "this workspace",
        joinWorkspaceTitle: "Join {{workspace}}",
        joinWorkspace: "Join a workspace",
        teammate: "A teammate",
        singleSubtitle:
          "{{inviter}} invited you to join this workspace as {{article}} {{role}}.",
        multipleSubtitle: "You have {{count}} pending invitation{{plural}}.",
        acceptFailed: "Failed to accept invitation. Please try again.",
        userAvatarAlt: "User",
        invitedBy: "Invited by",
        roleLabel: "Role:",
        join: "Join",
        joining: "Joining...",
        createWorkspace: "Create workspace",
        expired: "This invite has expired.",
        expiresToday: "This invite expires today.",
        expiresInOneDay: "This invite expires in 1 day.",
        expiresInDays: "This invite expires in {{days}} days.",
        joiningWorkspace: "Joining workspace...",
        checkingInvitation: "Checking your invitation...",
        invalidTitle: "This invite is no longer valid",
        invalidDescription: "This workspace invite is invalid or has expired.",
        goToDashboard: "Go to dashboard",
        differentAccountTitle: "This invite belongs to a different account",
        signedInAs: "You are signed in as",
        unknownEmail: "Unknown email",
        sentTo: "This invite was sent to",
        switchAccountDescription:
          "Log out and continue with the invited account to join {{workspace}}.",
        logOutAndSwitch: "Log out and switch account",
      },
      welcome: {
        title: "Your AI operator is ready to go.",
        description:
          "You can now start handing off day-to-day operations to it.",
        greeting: "Hi, I'm Bustly.",
        avatarSubtitle: "Ready to get to work.",
        avatarRole: "AI Operator",
        cta: "Set up Bustly",
      },
      intent: {
        title: "What should Bustly solve first?",
        description: "Choose the one issue you want off your plate first.",
        hintLine1: "Point me at the problem,",
        hintLine2: "and I'll start there.",
        otherPlaceholder: "Type what you want Bustly to help with...",
        setPriority: "Set priority",
        options: {
          ads: {
            label: "Ads are getting expensive",
            description: "Best match for acquisition efficiency",
          },
          conversion: {
            label: "Conversion is too low",
            description: "Landing pages, offers, and funnel fixes",
          },
          retention: {
            label: "Repeat purchase is weak",
            description: "Retention and lifecycle work",
          },
          execution: {
            label: "The team can’t keep up",
            description: "Execution support across the board",
          },
          other: {
            label: "Something else",
          },
        },
      },
      connect: {
        title: "Connect your business apps.",
        description:
          "Connect at least 4 apps to give Bustly a clear picture of your business and start your trial.",
        descriptionMore: "The more you connect, the more Bustly understands.",
        stepLabel: "Step 2 of 3 · Connect apps",
        hintLine1: "Show me the store,",
        hintLine2: "and I'll get up to speed fast.",
        categories: {
          storeOps: "Store Ops",
          advertising: "Advertising",
          emailMarketing: "Email Marketing",
          analytics: "Analytics",
          seo: "SEO",
          socialMedia: "Social Media",
          customers: "Customers",
          connectionCount: "{{count}} connected",
          descriptions: {
            storeOps: "Stores and commerce platforms",
            advertising: "Paid advertising platforms",
            emailMarketing: "Email campaigns and customer engagement",
            analytics: "Performance measurement and reporting",
            seo: "Search visibility and organic growth",
            socialMedia: "Social channels and audience engagement",
            customers: "Reviews and customer feedback",
          },
        },
        connectionProgress: "{{connected}} of {{required}} apps connected",
        connectedAppsLabel: "Connected apps",
        progressComplete: "Complete",
        statusLoadFailed:
          "We couldn't check your connected apps. Try again before continuing.",
        connect: "Connect",
        connected: "Connected",
        disconnect: "Disconnect",
        disconnecting: "Disconnecting...",
        connectFailed: "Failed to connect to {{platform}}",
        connectionNotCompleted:
          "Connection wasn’t completed. Click Connect when you’re ready to try again.",
        connectedSuccess: "Successfully connected to {{platform}}",
        disconnectFailed: "Failed to disconnect from {{platform}}",
        disconnectedSuccess: "Successfully disconnected from {{platform}}",
        disconnectTitle: "Disconnect {{tool}}?",
        disconnectDescription:
          "Disconnecting will stop data synchronization. You can reconnect at any time.",
        connectToolTitle: "Connect {{tool}}",
        connectToolAction: "Connect {{tool}}",
        disconnectToolAction: "Disconnect {{tool}}",
        storeHash: "Store Hash",
        storeHashPlaceholder: "abc123xyz",
        accountUuid: "Account UUID",
        accountUuidPlaceholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        platformDescriptions: {
          shopify: "Orders, products, inventory, and storefront health",
          woocommerce: "Storefront events, order flow, and fulfillment alerts",
          bigcommerce: "Catalog monitoring, checkout signals, and issue triage",
          magento: "Inventory visibility, orders, and merchandising ops",
          prestashop: "Catalog, orders, customers, inventory, and discounts",
        },
      },
      plan: {
        title: "Start with a 7-day trial",
        paidTitle: "Choose a plan to continue",
        loadingOptions: "Loading your plan options...",
        workspaceNotReady: "Workspace is not ready yet.",
        failedToStartCheckout: "Failed to start checkout.",
        failedToStartTrial: "Failed to start trial.",
        unavailableNow: "{{plan}} is not available right now.",
        startingPriceLabel: "From",
        startingPriceSuffix: "",
        startingAt: "/mo",
        renewalNote:
          "Payment method required. After 7 days, your plan renews automatically at the price shown. Upgrade anytime.",
        trialWorkUnits:
          "During your 7-day trial: {{value}} Work Units refreshed daily",
        checkoutRequired: {
          title: "Free trial unavailable",
          description:
            "This store isn't eligible for a free trial. Continue to checkout for a paid plan, or connect another store.",
          continueToCheckout: "Continue to checkout",
          connectAnotherStore: "Connect another store",
        },
        solo: {
          title: "Solo",
          startingPrice: "$199",
          workUnitsSummary: "2,780 Work Units / month",
          description:
            "For founders and solo operators hiring their first AI operator.",
          bullet1: "1 workspace user included",
          bullet2: "Works like a 3–5 year operator, available 24/7",
          bullet3: "Available 24/7",
          bullet4: "1 store connection included",
          bullet5: "400 Work Units refreshed daily",
          lowestTierBullet1: "1 workspace user included",
          lowestTierBullet2: "1 store connection included",
          lowestTierBullet3:
            "After your trial: {{value}} AI Operator Work Units per month for lighter daily operations",
          lowestTierBullet4: "Up to 5 app connections",
          lowestTierCapacity: "Handles about {{min}}–{{max}} routine operational tasks per month",
          cta: "Start with Solo",
          badge: "Choose Solo for 1 user and 1 store",
        },
        team: {
          title: "Team",
          startingPrice: "$1,499",
          workUnitsSummary: "20,980 Work Units / month",
          description:
            "For companies rolling out AI operators across the workspace.",
          bullet1: "Unlimited workspace users",
          bullet2: "Works like a 3–5 year operator, available 24/7",
          bullet3: "Available 24/7",
          bullet4: "Up to 3 store connections included",
          bullet5: "400 Work Units refreshed daily",
          lowestTierBullet1: "10 workspace users included",
          lowestTierBullet2: "Up to 2 store connections included",
          lowestTierBullet3:
            "After your trial: {{value}} shared AI Operator Work Units per month for higher team task volume",
          lowestTierBullet4: "Up to 10 app connections",
          lowestTierCapacity: "Handles about {{min}}–{{max}} team operational tasks per month",
          cta: "Start with Team",
          badge: "Choose Team for multiple users and up to 3 stores",
        },
        featureGroups: {
          workspace: "Workspace",
          workUnitTopUps: "On-demand Work Unit top-ups",
          builtInAiCapabilities: "Built-in AI capabilities",
          createImages: "Create images with Nano Banana 2",
          createVideos: "Create videos with Seedance 2.0",
          handleBusinessTasks: "Handle business tasks with Opus 4.8",
        },
      },
      contactSales: {
        title: "Talk to Sales",
        description: "Get a custom demo and pricing for your specific needs",
        submittedTitle: "Request Received!",
        submittedSubtitle: "Our team will be in touch shortly.",
        exploreDescriptionPrefix:
          "Once you connect your payment method, you can start your",
        enterpriseTrial: "7-day Enterprise Trial",
        exploreDescriptionSuffix: "immediately.",
        activateTrial: "Activate Enterprise Trial",
        submittedDescription:
          "Thank you for your interest in Bustly Enterprise. Our sales team will review your requirements and contact you within 24 hours.",
        firstName: "First name",
        lastName: "Last name",
        email: "Email",
        jobTitle: "Job title",
        jobTitlePlaceholder: "Marketing Director",
        companyName: "Company name",
        companyNamePlaceholder: "Acme Inc.",
        companySize: "Company size",
        companySizePlaceholder: "Select size...",
        companySizeOptions: {
          oneToTen: "1-10 employees",
          elevenToFifty: "11-50 employees",
          fiftyOneToTwoHundred: "51-200 employees",
          twoHundredOneToFiveHundred: "201-500 employees",
          fiveHundredPlus: "500+ employees",
        },
        brandCount: "Brands/stores",
        brandCountPlaceholder: "Select count...",
        brandCountOptions: {
          one: "1 brand/store",
          twoToFive: "2-5 brands/stores",
          sixToTwenty: "6-20 brands/stores",
          twentyOnePlus: "21+ brands/stores",
        },
        primaryUseCase: "Primary use case",
        primaryUseCasePlaceholder: "Select primary goal...",
        primaryUseCaseOptions: {
          analytics: "Unified Analytics & Reporting",
          attribution: "Multi-touch Attribution",
          forecasting: "Inventory & Sales Forecasting",
          automation: "Marketing Automation",
          other: "Other",
        },
        needs: "Specific needs / questions",
        needsPlaceholder: "Tell us about your requirements...",
        submitAndUnlockTrial: "Submit & unlock trial",
        submitRequest: "Submit request",
        legalText:
          "By submitting this form, you agree to our Terms of Service and Privacy Policy.",
        errors: {
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailRequired: "Email is required",
          emailInvalid: "Please enter a valid email address",
          companyRequired: "Company name is required",
          companySizeRequired: "Please select company size",
          brandCountRequired: "Please select number of brands/stores",
          primaryUseCaseRequired: "Please select a primary use case",
        },
      },
      industryRequest: {
        successTitle: "Thank you!",
        successSubtitle: "We've received your request.",
        successDescription:
          "A member of our team will reach out to you within 24 hours.",
        title: "Let's talk about your business",
        description:
          "Tell us a bit about yourself and we'll be in touch shortly.",
        firstName: "First name",
        lastName: "Last name",
        workEmail: "Work email",
        companyName: "Company name",
        companyNamePlaceholder: "Your company name",
        industry: "Industry",
        industryPlaceholder: "e.g. Healthcare, Logistics, Real Estate",
        companySize: "Company size",
        companySizePlaceholder: "Select company size",
        companySizeOptions: {
          oneToTen: "1-10 employees",
          elevenToFifty: "11-50 employees",
          fiftyOneToTwoHundred: "51-200 employees",
          twoHundredOneToFiveHundred: "201-500 employees",
          fiveHundredPlus: "500+ employees",
        },
        message: "Message (optional)",
        messagePlaceholder: "Tell us about your specific needs...",
        submitting: "Submitting...",
        submit: "Submit request",
        privacyText: "By submitting, you agree to our Privacy Policy.",
        submitError: "Failed to submit your request. Please try again.",
        errors: {
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailRequired: "Email is required",
          emailInvalid: "Please enter a valid email address",
          companyNameRequired: "Company name is required",
          industryRequired: "Industry is required",
        },
      },
      integrationRequest: {
        submittedTitle: "Request submitted",
        submittedDescription:
          "We'll track this request and notify you when it's available.",
        close: "Close",
        title: "Request an integration",
        description:
          "Tell us which tool you'd like to connect with Bustly and we'll prioritize adding it.",
        firstName: "First name",
        lastName: "Last name",
        workEmail: "Work email",
        toolName: "Tool or platform name",
        toolNamePlaceholder: "e.g. Notion, Airtable, Monday.com",
        toolCategory: "Tool category",
        selectCategory: "Select a category (optional)",
        categories: {
          ecommerce: "E-commerce",
          marketing: "Marketing & Advertising",
          analytics: "Analytics",
          crm: "CRM & Support",
          finance: "Finance & Accounting",
          other: "Other",
        },
        toolWebsite: "Tool website",
        toolWebsitePlaceholder: "https://example.com (optional)",
        reason: "Why do you need this integration?",
        reasonPlaceholder:
          "Tell us how you would use this integration... (optional)",
        submit: "Submit request",
        errors: {
          firstNameRequired: "First name is required",
          lastNameRequired: "Last name is required",
          emailRequired: "Email is required",
          emailInvalid: "Please enter a valid email address",
          toolNameRequired: "Tool or platform name is required",
        },
      },
      registrationGate: {
        title: "Configure Bustly for your D2C team",
        description:
          "Bustly is configured around your stores, team permissions, and daily operating workflows. Our product specialist will first understand your business goals, then guide the initial setup so your AI Operator is aligned with real outcomes from day one.",
        valueTitle: "What we’ll help you set up",
        values: {
          connections: {
            title: "Store and data connections",
            body: "How your stores, accounts, and core data sources should be connected",
          },
          access: {
            title: "Workspace and access control",
            body: "How to configure your workspace, roles, and access controls",
          },
          useCases: {
            title: "AI Operator use cases",
            body: "Which AI Operator use cases your team should start with",
          },
          plan: {
            title: "Plan and Work Units setup",
            body: "The right plan, Work Units setup, and expansion path for your team",
          },
        },
        formTitle: "Tell us about your business",
        submitLabel: "Contact Sales",
        logoutLabel: "Log out",
        successTitle: "Thank you!",
        successSubtitle: "Your request has been sent.",
        successMessage:
          "Our team will review your information and get back to you within 24 hours to schedule a personalized demo.",
        form: {
          firstNameLabel: "First Name",
          firstNamePlaceholder: "First name",
          lastNameLabel: "Last Name",
          lastNamePlaceholder: "Last name",
          emailLabel: "Email",
          emailPlaceholder: "you@company.com",
          workEmailLabel: "Work email",
          workEmailPlaceholder: "you@company.com",
          fullNameLabel: "Full name",
          fullNamePlaceholder: "Your name",
          workTitleLabel: "Work title",
          workTitlePlaceholder:
            "Founder, COO, Ecommerce Lead, Operations Manager...",
          companyNameLabel: "Company Name",
          companyNamePlaceholder: "Company name",
          storeUrlLabel: "Store or website URL",
          storeUrlPlaceholder: "https://yourstore.com",
          preferredContactMethodLabel: "Preferred contact method",
          preferredContactMethodPlaceholder: "Select contact method",
          preferredContactMethods: {
            email: "Email",
            phone: "Phone",
            whatsApp: "WhatsApp",
            weChat: "WeChat",
          },
          industryLabel: "Industry",
          industryPlaceholder: "e.g., Apparel, Beauty, Home Goods",
          companySizeLabel: "Company Size",
          companySizePlaceholder: "Select company size",
          companySizeOptions: {
            oneToTen: "1-10 employees",
            elevenToFifty: "11-50 employees",
            fiftyOneToTwoHundred: "51-200 employees",
            twoHundredOneToFiveHundred: "201-500 employees",
            fiveHundredPlus: "500+ employees",
          },
          messageLabel: "Message (optional)",
          messagePlaceholder:
            "Tell us about your store, team, and key use cases...",
          submittingLabel: "Submitting...",
          privacyText: "By submitting, you agree to our Privacy Policy.",
          notSpecifiedLabel: "Not specified",
          noMessageLabel: "No message provided",
          errorTitle: "Submission Failed",
          errorMessage:
            "There was an error submitting your request. Please try again.",
          errors: {
            firstNameRequired: "First name is required",
            lastNameRequired: "Last name is required",
            workEmailRequired: "Work email is required",
            fullNameRequired: "Full name is required",
            workTitleRequired: "Work title is required",
            emailRequired: "Email is required",
            emailInvalid: "Please enter a valid email address",
            companyNameRequired: "Company name is required",
            storeUrlRequired: "Store or website URL is required",
            storeUrlInvalid: "Please enter a valid URL",
            preferredContactMethodRequired:
              "Preferred contact method is required",
            industryRequired: "Industry is required",
          },
        },
      },
    },
    integrationsSettings: {
      integrations: "Integrations",
      connectedFilter: "Connected",
      storeOps: "Store Ops",
      advertising: "Advertising",
      emailMarketing: "Email Marketing",
      analytics: "Analytics",
      seo: "SEO",
      socialMedia: "Social Media",
      customers: "Customers",
      finance: "Finance",
      teamCollaboration: "Team Collaboration",
      searchIntegrations: "Search integrations...",
      clearSearch: "Clear search",
      searchResults: "Search results",
      searchResultsSummary:
        "{{count}} match{{plural}} across all categories",
      manageConnectionAccess: "Manage access",
      connectionFilter: "Connection filter",
      allConnections: "All connections",
      ownedByMe: "Owned by me",
      member: "Member",
      user: "User",
      role: "Role",
      connector: "Connector",
      connectionOwner: "Connection owner",
      you: "You",
      myConnectionsTitle: "App Connections",
      connectionAccessSubtitle:
        "Manage connected accounts, status, ownership, and who can use each connection.",
      userAccessForConnection: "Manage connection access",
      connectionAccessDescription:
        "Choose who can use this connection and who can manage access.",
      loadingConnectionAccess: "Loading connection access...",
      failedToLoadConnectionAccess: "Failed to load connection access.",
      connectionAccessUnavailable:
        "Connection access is unavailable for this connection.",
      failedToSaveConnectionAccess: "Failed to save connection access.",
      canManageAccess: "Manage access",
      canManageAccessHelp:
        "Users with this permission can set other users' access for this connection: No access, View only, or View & take action.",
      connectionOwnerManageAccessLocked:
        "Connection owners always have Manage access.",
      canManageAccessStatus: "Has access",
      cannotManageAccessStatus: "No access",
      connectionAccessLevel: "Connection access",
      manageOthersAccess: "Manage others' access",
      managePermission: "Manage Permission",
      descNoAccess: "This user cannot view or use this connection",
      descViewOnly: "This user can only view connection status and data",
      descViewAction: "This user can use and operate this connection",
      connectionManageAccessRequired:
        "This connection needs at least one person who can manage access.",
      applyToAll: "Set all to",
      searchMembers: "Search users...",
      noConnectionAccessRows: "No app connections to manage",
      noConnectionAccessRowsDescription:
        "Connect an app from Integrations, then return here to manage the connected account and teammate access.",
      noConnectionMatches: "No connections match this view",
      noConnectionMatchesDescription: "Try another filter or search term.",
      noAssignableMembers:
        "Invite teammates to give them access to this connection.",
      connectionAccessUpdated: "Connection access updated",
      noConnectedIntegrationsFound:
        'No connected integrations found for "{{query}}"',
      noIntegrationsFound: 'No integrations found for "{{query}}"',
      noIntegrationsInCategory: "No integrations in {{category}} yet",
      editLabel: "Edit Label",
      editLabelAction: "Edit label",
      label: "Label",
      labelPlaceholder: "e.g. North America",
      saving: "Saving",
      roleAccessForConnection: "Role access for this connection",
      roleAccessTooltip:
        "Set how each role can use this connection. Changes apply to everyone assigned to that role.",
      viewPermissionDetails: "View permission details",
      notSet: "Not set",
      accessByRole: "Access by role",
      roleCount: "{{count}} role{{plural}}",
      yourRole: "Your role",
      noUsers: "No users",
      updateAccess: "Update access",
      upgradeAddConnections: "Upgrade to add more connections",
      notNow: "Not now",
      upgradeToTeam: "Upgrade to Team",
      storeConnectionLimitReached: "Store connection limit reached",
      connectedStoreCount: "{{count}} connected store{{plural}}",
      otherIntegrationConnectionLimitReached:
        "Other integration connection limit reached",
      totalIntegrationConnectionLimitReached:
        "Total integration connection limit reached",
      upgrade: "Upgrade",
      connectToolTitle: "Connect {{tool}}",
      connectToolAction: "Connect {{tool}}",
      connectWithApiKey: "Connect with API key",
      connectFeishuTitle: "Connect Feishu",
      connectFeishuAction: "Connect Feishu",
      apiKeyConnectionDescription:
        "Paste your {{tool}} API key below.",
      apiKey: "API Key",
      apiKeyPlaceholder: "Paste your {{tool}} API key",
      okendoUserId: "User ID",
      okendoUserIdPlaceholder: "Paste Okendo User ID",
      okendoMerchantRestApiKey: "Merchant REST API Key",
      okendoMerchantRestApiKeyPlaceholder: "Paste Merchant REST API Key",
      showOkendoMerchantRestApiKey: "Show Okendo Merchant REST API Key",
      hideOkendoMerchantRestApiKey: "Hide Okendo Merchant REST API Key",
      connectionLabel: "Connection label",
      accountLabel: "Account Label",
      optional: "Optional",
      accountLabelPlaceholder: "Acme Growth Workspace",
      connect: "Connect",
      feishuAppId: "App ID",
      feishuAppSecret: "App Secret",
      feishuAppSecretPlaceholder: "Paste App Secret",
      toolStoreUrl: "{{tool}} Store URL",
      shopifyLabelPlaceholder: "e.g. North America Store",
      genericToolLabelPlaceholder: "e.g. {{tool}} US Account",
      connectionDetailsTitle: "Connection Details: {{tool}}",
      feishuCustomAppTitle: "Connect Feishu",
      appIdClientId: "App ID / Client ID",
      appSecret: "App Secret",
      appSecretPlaceholder: "Paste app secret",
      storeUrl: "Store URL",
      prestashopCredentialDescription:
        "Orders, products, customers, and store data use a PrestaShop Webservice key. Bustly only verifies that the key is valid; available skills follow the permissions you grant in PrestaShop.",
      prestashopStoreUrl: "Store URL",
      prestashopStoreUrlPlaceholder: "https://shop.example.com",
      prestashopWebserviceKey: "Webservice Key",
      prestashopWebserviceKeyPlaceholder: "PrestaShop Webservice key",
      showPrestaShopWebserviceKey: "Show Webservice key",
      hidePrestaShopWebserviceKey: "Hide Webservice key",
      account: "Account",
      myAccess: "My access",
      connectionStatus: "Connection status",
      action: "Action",
      noAccountSelected: "No Account Selected",
      viewConnectionDetails: "View Connection Details",
      roleAccess: "Role access",
      disconnecting: "Disconnecting...",
      disconnect: "Disconnect",
      howItWorks: "How it works",
      whatBustlyCanView: "What can Bustly view?",
      whatBustlyCanDo: "What can Bustly help do?",
      connected: "Connected",
      disconnected: "Disconnected",
      soon: "Soon",
      connecting: "Connecting...",
      waitingForRegistration: "Waiting for registration...",
      returnAfterCreatingAccount: "Return here after creating your account.",
      connectNow: "Connect now",
      addConnection: "Add connection",
      attentionNeeded: "Attention needed",
      error: "Error",
      connectedAt: "Connected at",
      storeLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}}. Choose a plan with a higher store limit to connect another store.",
      storeLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}}. Contact Sales if you need to connect more stores.",
      storeLimitPlansFallbackDescription:
        "Your current plan has reached its store connection limit. Choose a plan with a higher store limit to connect another store.",
      storeLimitSalesFallbackDescription:
        "Your current plan has reached its store connection limit. Contact Sales if you need to connect more stores.",
      otherIntegrationLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}} other integration connections. Choose a plan with a higher integration limit to add another connection.",
      otherIntegrationLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}} other integration connections. Contact Sales if you need more connections.",
      otherIntegrationLimitPlansFallbackDescription:
        "Your current plan has reached its other integration connection limit. Choose a plan with a higher integration limit to add another connection.",
      otherIntegrationLimitSalesFallbackDescription:
        "Your current plan has reached its other integration connection limit. Contact Sales if you need more connections.",
      totalIntegrationLimitPlansDescription:
        "Your {{plan}} plan supports up to {{limit}} total integration connections. Choose a plan with a higher total connection limit to add another connection.",
      totalIntegrationLimitSalesDescription:
        "Your {{plan}} plan supports up to {{limit}} total integration connections. Contact Sales if you need more connections.",
      totalIntegrationLimitPlansFallbackDescription:
        "Your current plan has reached its total integration connection limit. Choose a plan with a higher total connection limit to add another connection.",
      totalIntegrationLimitSalesFallbackDescription:
        "Your current plan has reached its total integration connection limit. Contact Sales if you need more connections.",
      pluginStoreLimitDescription:
        "Your Plugin plan includes {{limit}}. This Plugin workspace does not have self-serve upgrades.",
      pluginOtherIntegrationLimitDescription:
        "Your Plugin plan supports up to {{limit}} other app connections. This Plugin workspace does not have self-serve upgrades.",
      pluginTotalIntegrationLimitDescription:
        "Your Plugin plan supports up to {{limit}} app connections. This Plugin workspace does not have self-serve upgrades.",
      pluginIntegrationLimitReached:
        "This Plugin workspace has reached its plan limit. Self-serve upgrades are not available for this workspace.",
      roleAccessAriaLabel: "{{label}} for {{connection}}",
      dontHaveAccountSignUp: "Don't have an account? Sign up",
      klaviyoUnavailable: "Klaviyo unavailable",
      klaviyoUnavailableDescription:
        "Klaviyo reports this account is unavailable. Re-enable the account in Klaviyo, then reconnect or refresh Bustly.",
      descriptionTab: "Description",
      connectionsTab: "Connections",
      connectionCount: "{{count}} connection{{plural}}",
      showingConnectionsRange:
        "Showing {{start}} to {{end}} of {{total}} connections",
      userCount: "{{count}} user{{plural}}",
      thisIntegration: "this integration",
      teamPlan: "Team plan",
      storeHash: "Store Hash",
      accountUuid: "Account UUID",
      idLabel: "ID",
      nameLabel: "Name",
      labelUpdateFailed: "Label Update Failed",
      failedToUpdateIntegrationLabel: "Failed to update integration label",
      connectedSuccess: "Successfully connected to {{tool}}",
      unsupportedIntegration: "Unsupported integration: {{tool}}",
      connectPermissionDenied:
        "Permission Denied: You do not have permission to connect integrations",
      connectionFailed: "Connection Failed",
      failedToConnectToTool: "Failed to connect to {{tool}}",
      disconnectTitle: "Disconnect {{target}}?",
      disconnectSubtitle:
        "Disconnecting will stop data synchronization. You can reconnect at any time.",
      disconnectPermissionDenied:
        "Permission Denied: You do not have permission to disconnect integrations",
      disconnectedSuccess:
        "{{tool}} Disconnected: Successfully disconnected from {{tool}}",
      disconnectFailed: "Disconnect Failed",
      failedToDisconnectFromTool: "Failed to disconnect from {{tool}}",
      currentTrialCantBeUsedHere: "Current trial can't be used here",
      currentTrialCantBeUsedHereDescription:
        "Looks like this app was already tried, so this trial can't continue here. You can still continue with a subscription plan.",
      tryAnotherApp: "Try another app",
      storeBoundToAnotherWorkspace:
        "This store is already bound to another workspace.",
      roleCreatedSuccessfully: "Role created successfully",
      failedToCreateRole: "Failed to create role",
      updateRoleAccessTitle: "Update role access?",
      updateRoleAccessSubtitle:
        "This will update access for {{count}} role{{plural}} on this connection.",
      update: "Update",
      roleAccessSavedSuccessfully: "Role access saved successfully",
      failedToSaveRoleAccess: "Failed to save role access",
      upgradeAddConnectionsDescription:
        "To connect multiple accounts under {{tool}}, upgrade your workspace to {{plan}}.",
      connectRestrictedToolTitle: "Connect {{tool}}",
      connectRestrictedToolDescription:
        "{{tool}} integration is only available on the Team plan. Upgrade your workspace to start connecting team collaboration tools.",
      readAndWrite: "Read & write",
      readOnly: "Read only",
      writeOnly: "Write only",
      noAccess: "No access",
      viewOnly: "View only",
      viewAndTakeAction: "View & take action",
      connectedRecentlyBy: "Connected recently by {{user}}",
      connectedRecently: "Connected recently",
      connectedByOn: "Connected by {{user}} on {{date}}",
      connectedOn: "Connected on {{date}}",
      mcpCustomTitle: "MCP Configuration",
      mcpJsonImportTitle: "Import MCP from JSON",
      mcpUrlTitle: "Add MCP by URL",
      mcpServerName: "Server name",
      mcpServerNamePlaceholder: "e.g. My Custom Server",
      mcpTransportType: "Transport type",
      mcpServerUrl: "Server URL",
      mcpAdvancedSettings: "Advanced settings",
      mcpAddCustomHeader: "Add custom header",
      mcpHeaderName: "Header name",
      mcpHeaderValue: "Header value",
      mcpCustomMethod: "Custom MCP",
      mcpCustomMethodDescription: "Manually configure an HTTP MCP server and optional authentication header.",
      mcpJsonMethod: "Import from JSON",
      mcpJsonMethodDescription: "Paste an MCP config JSON and import URL-based servers.",
      mcpUrlMethod: "Add by URL",
      mcpUrlMethodDescription: "Quickly add a trusted remote MCP server by URL.",
      mcpOAuthDescription: "Connect with the server's standard authorization flow. Manual setup is available only when the server does not support it.",
      mcpOAuthMethod: "OAuth authorization",
      mcpConnectWithOAuth: "Connect",
      mcpOAuthFailed: "We couldn't start authorization for this MCP server. Try again.",
      mcpRefreshFailed: "Authorization finished, but the connector list could not refresh. Refresh this page in a moment.",
      mcpManualSetupTitle: "Set up MCP manually",
      mcpManualSetupDescription: "This server does not support standard authorization. Choose the access method it provides.",
      mcpManualAuthType: "Access method",
      mcpManualNone: "No credentials",
      mcpManualBearer: "Bearer token",
      mcpManualApiKey: "API key header",
      mcpManualSecret: "Credential",
      mcpOAuthReturnTitle: "Authorization complete",
      mcpOAuthReturnDescription: "You can close this window.",
      mcpOAuthReturnSuccessTitle: "Authorization received",
      mcpOAuthReturnSuccessDescription: "Bustly is confirming the connection. You can close this window.",
      mcpOAuthReturnErrorTitle: "Authorization wasn't completed",
      mcpOAuthReturnErrorDescription: "Close this window and try connecting again from Integrations.",
      mcpOAuthReturnMalformedTitle: "This authorization return is incomplete",
      mcpOAuthReturnMalformedDescription: "Close this window and start the connection again from Integrations.",
      mcpOAuthReturnClose: "Close window",
      mcpTest: "Test",
      mcpTestSuccess: "MCP connection is working.",
      mcpTestFailed: "We couldn't test this MCP connection. Try again.",
      mcpSync: "Sync",
      mcpSyncSuccess: "MCP tools synced.",
      mcpSyncFailed: "We couldn't sync MCP tools. Try again.",
      mcpPermissionRequired: "You don't have permission to manage MCP connections.",
      mcpSaving: "Saving...",
      mcpImport: "Import",
      mcpSaveAndSync: "Save and sync",
      mcpInvalidConfiguration: "Invalid MCP configuration.",
      mcpInvalidJson: "Invalid MCP JSON.",
      mcpLoadFailed: "Failed to load MCP connectors.",
      mcpSaveFailed: "Failed to save MCP connector.",
      mcpConnectedSuccess: "MCP connector saved.",
      mcpImportSuccess: "{{count}} MCP connectors imported.",
      mcpToolCount: "{{count}} tool{{plural}}",
      mcpToolsSynced: "Tools synced",
      mcpNoConnectors: "No MCP connectors yet.",
      mcpManageTools: "Manage tools",
      mcpHideTools: "Hide tools",
      mcpToolName: "Tool",
      mcpToolEnabled: "Enabled",
      mcpToolAccess: "Access",
      mcpToolRisk: "Risk",
      mcpToolRequireConfirmation: "Confirm",
      mcpMemberAccess: "Member access",
      mcpToolUpdateSuccess: "MCP tool settings saved.",
      mcpToolUpdateFailed: "Failed to save MCP tool settings.",
      googleAdsAccountSelection: {
        title: "Select Google Ads accounts",
        updateFailed: "Failed to update Google Ads accounts.",
        updateSuccess: "Google Ads accounts updated.",
        loadingAccounts: "Loading accounts",
        noActiveConnection: "No active Google Ads connection found.",
        selectAllCustomers: "Select all customers",
        customer: "Customer",
        customerId: "Customer ID",
        noEligibleAccounts:
          "No eligible Google Ads customer accounts found.",
        save: "Save",
      },
      toolDescriptions: {
        shopify:
          "Connect Shopify so Bustly can keep track of your store operations across products, orders, customers, inventory, and discounts. It can answer store questions, spot changes that need attention, and help update listings, stock, discounts, or orders when you ask.",
        bigCommerce:
          "Connect BigCommerce so Bustly can understand your store activity across catalog, orders, customers, and inventory. It can answer business questions, surface store issues, and help update products or stock when you ask.",
        wooCommerce:
          "Connect WooCommerce so Bustly can keep your store data in context across products, orders, customers, and inventory. It can answer store questions, track activity, and help edit listings, update stock, or handle order updates when you ask.",
        magento:
          "Connect Magento so Bustly can understand your catalog, inventory, customer, and order context. It can monitor store activity, answer operational questions, and help create or update catalog products or stock when you ask.",
        prestashop:
          "Connect PrestaShop with a Webservice key so Bustly can read the store data you grant, including catalog, orders, customers, stock, and discounts. After connection, agents can answer operational questions, pull store reports, and help update products, variants, inventory, order details, customers, or promotions within the permissions configured in PrestaShop.",
        wix: "Connect Wix so Bustly can understand your store content, products, orders, collections, and inventory. It can answer questions about your store, track sales activity, and help update listings, details, or stock when you ask.",
        googleAds:
          "Connect Google Ads so Bustly can keep track of your campaign performance across campaigns, ad groups, keywords, ads, assets, and spend. It can answer marketing questions, spot performance changes, and help create or update campaigns, ad groups, keywords, ads, and assets when you ask.",
        tikTokAds:
          "Connect TikTok Ads so Bustly can understand ad performance across advertiser accounts, campaigns, ad groups, ads, and reports. It can track spend, impressions, and results, helping you spot what is changing across your TikTok campaigns.",
        metaAds:
          "Connect Meta Ads so Bustly can keep track of performance across campaigns, ad sets, creatives, audiences, and budgets. It can answer campaign questions, spot changes in performance, and help pause, resume, or adjust campaigns when you ask.",
        linkedInAds:
          "Connect LinkedIn Ads so Bustly can understand performance across ad accounts, campaign groups, campaigns, creatives, matched audiences, targeting, and analytics. It can answer campaign questions, review performance changes, help create or boost ads, and update campaign or campaign group status, budgets, and settings when you ask.",
        pinterestAds:
          "Connect Pinterest Ads so Bustly can understand your ad performance across ad accounts, campaigns, ad groups, promoted Pins, audiences, targeting, and analytics. It can answer campaign questions, review performance changes, and help update campaign or ad group status, budgets, schedules, targeting, and settings when you ask.",
        xAds: "Connect X Ads so Bustly can track performance across ad accounts, campaigns, line items, promoted tweets, targeting, and analytics. It can answer advertising questions, surface performance changes, help create or promote tweets, and update campaign or line item status, budgets, and settings when you ask.",
        youtubeAds:
          "Connect YouTube Ads so Bustly can understand channel, video, comment, and analytics activity.",
        snapchatAds:
          "Connect Snapchat Ads so Bustly can review account, post, comment, and analytics activity.",
        googleSearchConsole:
          "Connect Google Search Console so Bustly can understand how people find you through organic search. It can review queries, pages, rankings, and indexing signals to help you spot search opportunities and technical issues.",
        googleAnalytics:
          "Connect Google Analytics 4 so Bustly can understand your traffic, audience, and conversion patterns across accounts, properties, audiences, custom dimensions, and reports. It can answer analytics questions, generate reports, and help manage properties, audiences, or custom dimensions when you ask.",
        klaviyo:
          "Connect Klaviyo so Bustly can understand your email marketing and customer engagement across profiles, lists, segments, campaigns, flows, catalog items, templates, metrics, events, and reports. It can review performance, answer marketing questions, and help manage profiles, lists, list membership, segments, campaigns, flows, templates, metrics, and events when you ask.",
        mailchimp:
          "Connect Mailchimp so Bustly can understand your email marketing across account data, audiences, audience members, segments, campaigns, templates, and reports. It can review campaign performance, answer audience questions, and help manage audiences, members, tags, segments, campaigns, schedules, sends, and templates when you ask.",
        omnisend:
          "Connect Omnisend with an API key so Bustly can review lifecycle marketing data across contacts, campaigns, orders, and products.",
        okendo:
          "Connect Okendo with User ID and API Key so Bustly can review product reviews, ratings, customer feedback, survey responses, quiz responses, and webhook metadata. Write access is limited to supported review moderation, review replies, and Shopify-backed review groups.",
        insiderOne:
          "Connect Insider One so Bustly can review customer engagement, campaign, and analytics data. If OAuth is not available for your account yet, you can connect with an API key instead.",
        emarsys:
          "Connect Emarsys so Bustly can understand customer engagement across campaigns, contacts, messages, and analytics.",
        judgeme:
          "Connect Judge.me so Bustly can read Shopify product review data, product review summaries, and Judge.me product records. With write access, Bustly can create reviews and send manual review requests when you ask.",
        facebookPage:
          "Connect Facebook Page so Bustly can understand your Page content and engagement across posts, comments, Messenger conversations, Messenger menu, and Page insights. It can review post performance, help create Page posts, drafts, or scheduled posts, and reply to comments when you ask.",
        instagram:
          "Connect Instagram so Bustly can understand your account, media, audience, comments, messages, ice breakers, and insights. It can review content performance, help create media posts, drafts, or scheduled posts, and reply to comments when you ask.",
        feishu:
          "Connect a Feishu custom app so Bustly can work with authorized calendar events, docs/wiki/drive, sheets, Bitable records, and tasks. Available actions depend on the permissions configured and granted for the Feishu app. Mail and IM sending are not available from this integration entry yet.",
        figma:
          "Connect Figma with a personal access token so Bustly can read authorized design files, nodes, image renders, comments, versions, components, styles, and dev resources available to the token scopes. It can support design collaboration, design-system, and multilingual product-listing handoff workflows through Figma REST APIs; it is not a full visual canvas editor.",
        customMcp:
          "Connect remote MCP servers so Bustly can discover their tools and make them available in the workspace under the configured access rules.",
        slack: "Receive instant notifications and alerts in your workspace.",
        semrush: "Track SEO performance and domain analytics data.",
        similarweb:
          "Connect Similarweb with an API key so Bustly can review website traffic, market intelligence, and competitive analytics data.",
        ahrefs:
          "Connect Ahrefs with an API key so Bustly can review SEO usage, backlink intelligence, and domain rating data.",
        aliexpress:
          "Connect AliExpress so Bustly can help source products for your store. It can search by keyword or image, review product details, pricing, availability, images, and SKU variants, and help compare sourcing options when you ask.",
        squarespace: "Manage your Squarespace commerce operations.",
        default:
          "Connect {{tool}} to enhance your Bustly workspace capabilities.",
      },
    },
    integrationCapabilityItems: {
      storeInformationAndSettings: "Store information and settings",
      productCatalogAndVariants: "Product catalog and variants",
      catalogProductsAndVariants: "Catalog products and variants",
      ordersAndOrderDetails: "Orders and order details",
      customerProfiles: "Customer profiles",
      customerProfilesAndSegments: "Customer profiles and segments",
      inventoryLevels: "Inventory levels",
      discountCodes: "Discount codes",
      storeAnalyticsAndPerformanceData: "Store analytics and performance data",
      collections: "Collections",
      createUpdateOrDeleteProducts: "Create, update, or delete products",
      createUpdateOrDeleteCatalogProducts: "Create, update, or delete catalog products",
      uploadProductImages: "Upload product images",
      publishProductsToSalesChannels: "Publish products to sales channels",
      updateProductVariants: "Update product variants",
      adjustInventoryLevels: "Adjust inventory levels",
      updateOrderDetails: "Update order details",
      editOrderLineItems: "Edit order line items",
      updateCustomerInformation: "Update customer information",
      updateCollections: "Update collections",
      createUpdateOrDeleteDiscounts: "Create, update, or delete discounts",
      productSearchResults: "Product search results",
      productDetails: "Product details",
      pricingAndAvailability: "Pricing and availability",
      productImages: "Product images",
      skuVariants: "SKU variants",
      adAccounts: "Ad accounts",
      customerAccounts: "Customer accounts",
      advertisers: "Advertisers",
      campaigns: "Campaigns",
      adSets: "Ad sets",
      adGroups: "Ad groups",
      ads: "Ads",
      adCreatives: "Ad creatives",
      customAudiences: "Custom audiences",
      targeting: "Targeting",
      analytics: "Analytics",
      comments: "Comments",
      conversionDestinations: "Conversion destinations",
      assets: "Assets",
      keywords: "Keywords",
      reports: "Reports",
      promotedPins: "Promoted Pins",
      audiences: "Audiences",
      lineItems: "Line items",
      promotedTweets: "Promoted tweets",
      updateCampaigns: "Update campaigns",
      updateAdSets: "Update ad sets",
      updateAdGroups: "Update ad groups",
      updateLineItems: "Update line items",
      createOrUpdateCampaigns: "Create or update campaigns",
      createOrUpdateAdGroups: "Create or update ad groups",
      createOrUpdateAds: "Create or update ads",
      createAndAttachAssets: "Create and attach assets",
      createOrUpdateKeywords: "Create or update keywords",
      createOrPromoteTweets: "Create or promote tweets",
      pages: "Pages",
      posts: "Posts",
      pageInsights: "Page insights",
      messengerConversations: "Messenger conversations",
      messengerPersistentMenu: "Messenger persistent menu",
      createPagePostsDraftsAndScheduledPosts: "Create Page posts, drafts, and scheduled posts",
      replyToComments: "Reply to comments",
      account: "Account",
      media: "Media",
      insights: "Insights",
      audience: "Audience",
      messages: "Messages",
      iceBreakers: "Ice breakers",
      createMediaPostsDraftsAndScheduledPosts: "Create media posts, drafts, and scheduled posts",
      profiles: "Profiles",
      lists: "Lists",
      segments: "Segments",
      flows: "Flows",
      catalogItems: "Catalog items",
      templates: "Templates",
      metrics: "Metrics",
      events: "Events",
      manageProfiles: "Manage profiles",
      manageLists: "Manage lists",
      manageSegments: "Manage segments",
      manageCampaigns: "Manage campaigns",
      manageFlows: "Manage flows",
      manageTemplates: "Manage templates",
      updateMetrics: "Update metrics",
      manageEvents: "Manage events",
      audienceMembers: "Audience members",
      createAndUpdateAudiences: "Create and update audiences",
      addUpdateUnsubscribeAndTagAudienceMembers: "Add, update, unsubscribe, and tag audience members",
      contacts: "Contacts",
      manageContacts: "Manage contacts",
      manageOrders: "Manage orders",
      accounts: "Accounts",
      properties: "Properties",
      customDimensions: "Custom dimensions",
      createAndUpdateCustomDimensions: "Create and update custom dimensions",
      sites: "Sites",
      searchAnalytics: "Search analytics",
      inspectUrls: "Inspect URLs",
      sitemaps: "Sitemaps",
      addOrRemoveSites: "Add or remove sites",
      submitOrRemoveSitemaps: "Submit or remove sitemaps",
      requestIndexingUpdates: "Request indexing updates",
      domainAnalytics: "Domain analytics",
      keywordRankings: "Keyword rankings",
      backlinkInsights: "Backlink insights",
      competitorResearch: "Competitor research",
      websiteTraffic: "Website traffic",
      marketIntelligence: "Market intelligence",
      competitorAnalytics: "Competitor analytics",
      seoUsage: "SEO usage",
      domainRating: "Domain rating",
      reviews: "Reviews",
      reviewSummaries: "Review summaries",
      customerFeedbackProfiles: "Customer feedback profiles",
      surveyResponses: "Survey responses",
      quizResponses: "Quiz responses",
      webhookSubscriptions: "Webhook subscriptions",
      manageReviews: "Manage reviews",
      createReviewGroups: "Create review groups",
      sendReviewRequests: "Send review requests",
      feishuAuthorizedUserAndContactProfiles: "Read authorized user and contact profile details",
      feishuCalendars: "Read calendars, event ranges, attendees, and free/busy status",
      feishuDocs: "Read docs, wiki nodes, comments, and drive metadata",
      feishuSpreadsheets: "Read spreadsheet metadata, ranges, and exported content",
      feishuBitable: "Read Bitable apps, tables, fields, views, and records",
      feishuTasks: "Read tasks and tasklists",
      feishuManageCalendarAttendees: "Create, update, reply to, and manage event attendees",
      feishuManageDocsAndWiki: "Create, update, move, copy, and comment on docs or wiki content",
      feishuWriteSpreadsheets: "Create spreadsheets and write or append ranges",
      feishuManageBitableRecords: "Create, update, and delete Bitable records and manage Bitable schema",
      feishuManageTasks: "Create and update tasks, tasklists, comments, subtasks, and tasklist members",
    },
    inviteAccept: {
      alreadyAccepted:
        "This invitation has already been accepted. Continue to sign in and open the workspace.",
      backToSignIn: "Back to Sign In",
      cancelledDescription:
        "This workspace invite has been cancelled by the workspace owner.",
      checkingInvitation: "Checking your invitation...",
      close: "Close",
      createWorkspace: "Create workspace",
      differentEmail:
        "This invite belongs to a different email. Please sign out and continue with the invited account.",
      invalidDescription:
        "This workspace invite is invalid, expired, or has been cancelled.",
      invalidTitle: "Link Expired",
      join: "Join",
      joinTitle: "Join a workspace",
      loadFailed: "We could not load this invitation.",
      logOut: "Log out",
      memberRole: "Member",
      missingToken: "This invite link is missing an invitation token.",
      needHelp: "Need help? Contact our support team",
      roleLabel: "Role:",
      sentTo: "Sent to",
      singleSubtitle:
        "{{inviter}} invited you to join this workspace as {{article}} {{role}}.",
      startingLogin: "Starting login...",
      startFailed: "We could not start this invitation login.",
      teammate: "A teammate",
      thisWorkspace: "this workspace",
      unavailableTitle: "Link Unavailable",
    },
    planText: {
      basic: "Basic",
      trial: "Trial",
      expired: "Expired",
      startSubscription: "Start subscription",
      upgrade: "Upgrade",
      renew: "Renew",
      manage: "Manage",
      freeTrialExpiredOn: "Free trial expired on {{date}}.",
      expiredOn: "Expired on {{date}}.",
      daysLeft: "{{days}} days left.",
      expiresOn: "Expires on {{date}}.",
      autoRenewsOn: "Your plan auto-renews on {{date}}.",
      trialEndsToday: "Free trial ends today.",
      oneDayLeftInTrial: "Free trial ends in 1 day.",
      daysLeftInTrial: "Free trial ends in {{days}} days.",
    },
    billing: {
      permissionDenied: "Permission Denied",
      noPermissionViewSubscriptionDetails:
        "You do not have permission to view subscription details.",
      billingAccessLoading: "Billing & Plans is loading.",
      billingAccessLoadFailed: "We couldn't load Billing & Plans",
      billingAccessLoadFailedDescription:
        "Try again to continue managing this workspace's plan.",
      currentPlan: "Current plan",
      currentBillingPlanLoading: "Current billing plan is loading.",
      active: "Active",
      pluginPlanLimitUsers: "{{count}} workspace user",
      pluginPlanLimitStores: "{{count}} connected store",
      pluginPlanLimitApps: "Up to {{count}} app connections",
      viewOnly: "View only",
      billingHistory: "Billing history",
      date: "Date",
      description: "Description",
      type: "Type",
      amount: "Amount",
      status: "Status",
      billingHistoryLoading: "Billing history is loading.",
      billingHistoryUnavailable: "Billing history is not available.",
      showingHistoryRange: "Showing {{start}} to {{end}} of {{total}} records",
    },
    topup: {
      permissionDeniedBuyCredits: "You do not have permission to buy Work Units.",
      joinWorkspaceToBuyCredits: "Join this workspace to buy Work Units.",
      availableForSoloAndTeam:
        "Work Unit top-up is available for Solo and Team plans.",
      restorePaidPlan: "Restore your paid plan to buy Work Units.",
      availableForActivePaidPlans:
        "Work Unit top-up is available for active paid plans.",
      checkoutUrlMissing: "Checkout URL is missing.",
      failedToStartCheckout: "Failed to start checkout.",
      title: "Buy more Work Units",
      description:
        "Add Work Units to keep using Bustly beyond your plan limits.",
      amountToAdd: "Amount to add",
      credits: "Work Units",
      next: "Next",
    },
    usage: {
      agentSelfImproving: "Agent self improving",
      dailyCredits: "Daily Work Units",
      monthlyCredits: "Monthly Work Units",
      shopifyPendingPaymentTooltipLabel: "Why is there a daily limit?",
      shopifyPendingPaymentTooltip:
        "Shopify payment is approved, but Bustly hasn't received it yet. {{dailyCredits}} Work Units refresh daily for now. Remaining monthly Work Units unlock after payment is received.",
      shopifyPendingPaymentTooltipWithoutLimit:
        "Shopify payment is approved, but Bustly hasn't received it yet. Plan Work Units refresh daily for now. Remaining monthly Work Units unlock after payment is received.",
      planStatusActive: "Active",
      planStatusTrial: "Trial",
      planStatusEnding: "Ending",
      planStatusExpired: "Expired",
      planStatusLimitReached: "Limit reached",
      grantStatusInUse: "In use",
      grantStatusAvailable: "Available",
      grantStatusExpired: "Expired",
      grantStatusUsedUp: "Used up",
      failedToLoadCreditOptions: "Failed to load Work Unit options.",
      resetsOn: "Next reset: {{date}} ({{timezone}})",
      endsOn: "Ends on {{date}}",
      expiresOn: "Expires on {{date}} ({{timezone}})",
      aboutCredits: "About Work Units",
      startSubscriptionToContinue: "Start subscription to continue",
      upgradeToContinue: "Upgrade to continue",
      purchasedCreditsSeparate:
        "Purchased Work Units stay separate from your monthly plan balance",
      noPurchasedCreditsAvailable:
        "Use purchased Work Units to continue using Bustly beyond your plan limits",
      purchasedCreditsSummary: "Purchased Work Units summary",
      expShort: "exp.",
      totalRemaining: "Total remaining",
      purchasedCredits: "Purchased Work Units",
      purchasedCreditsUsageOrder:
        "Purchased Work Units are used after monthly Work Units.",
      usePurchasedCreditsDescription:
        "Use purchased Work Units to continue using Bustly beyond your plan limits",
      enabledAutoReload: "Enabled auto reload",
      savedAutoReloadSettings: "Saved auto reload settings",
      disabledAutoReload: "Disabled auto reload",
      amountRemainingSuffix: "remaining",
      creditsRemaining: "Work Units remaining",
      yourPlan: "Plan Work Units",
      credit: "Purchased Work Units",
      empty: "Empty",
      available: "Available",
      frozen: "Frozen",
      expired: "Expired",
      blocked: "Blocked",
      permissionDenied: "Permission Denied",
      noPermissionViewUsageDetails:
        "You do not have permission to view usage details.",
      noExtraCreditsDescription: "No extra Work Units available.",
      frozenCreditsDescription:
        "Work Units are frozen. Restore your paid subscription to use them.",
      expiredCreditsDescription: "Your purchased extra Work Units have expired.",
      purchaseBlockedDescription: "Purchase blocked on free or expired plans.",
      upgradeToPurchase: "Upgrade your plan to purchase extra Work Units",
      buyCreditsTrialTooltip:
        "Only workspaces with an active Solo or Team subscription can buy Purchased Work Units",
      buyCreditsPermissionTooltip:
        "Only Workspace Owners or users with Manage billing permission can buy Work Units",
      autoReload: "Auto reload",
      autoReloadCredit: "Auto-reload Work Units",
      active: "Active",
      autoReloadDescription:
        "Automatically add Work Units when you reach your minimum balance.",
      usageRecord: "Usage record",
      user: "User",
      task: "Task",
      time: "Last updated",
      creditsChange: "Total Work Units change",
      noUsageEvents: "Your task usage will show up here.",
      showingEventsRange: "Showing {{start}} to {{end}} of {{total}} events",
      showingZeroEvents: "Showing {{total}} events",
      purchasedCreditPackages: "Purchased Work Unit packages",
      purchasedCreditPackagesDescription:
        "Each purchased Work Unit package expires 12 months after purchase.",
      total: "Total",
      used: "Used",
      remaining: "Remaining",
      expiresAt: "Expires at",
      status: "Status",
      noPurchasedCreditPackages: "No purchased Work Unit packages yet.",
      showingRangeOfTotal: "Showing {{start}}-{{end}} of {{total}}",
      viewDetails: "View details",
      received: "Received",
      balance: "Balance",
      minimumBalance: "Minimum balance",
      minimumBalanceDescription:
        "Auto reload triggers when your Work Unit balance goes below this amount.",
      targetBalance: "Target balance",
      targetBalanceDescription:
        "Auto reload brings your Work Unit balance back up to this amount.",
      minimumPurchase:
        "At least {{credits}} Work Units will be purchased, equivalent to",
      autoChargeDescription:
        "Bustly will charge your payment method automatically when you reach your minimum balance.",
      autoReloadInitialPurchase:
        "Enabling auto reload will trigger a one-time purchase of {{credits}} Work Units to reach your target balance. Estimated cost:",
      turnOff: "Turn off",
      turnOn: "Turn on",
      credits: "Work Units",
    },
    downloads: {
      usingBustlyAnywhere: "Using Bustly anywhere",
      downloadDescription:
        "Download Bustly Desktop to run tasks and manage your workspaces with a more integrated native experience.",
      downloadDescriptionShort: "Download Bustly Desktop to start tasks and review the work.",
      downloadClient: "Download",
    },
  },
  startup: {
    configuring: "Configuring your workspace...",
    openingWorkspace: "Opening your workspace...",
    ready: "Just a moment, and Bustly will be ready to work.",
    problem: "Bustly hit a problem while configuring your workspace.",
  },
  updater: {
    checkForUpdates: "Check for Updates",
    checking: "Checking",
    checkingForUpdates: "Checking for updates...",
    checkFailed: "Failed to check for updates.",
    preparing: "Preparing update...",
    installFailed: "Install failed",
    installing: "Installing update...",
    failedDescription: "The updater could not finish this release.",
    installingDescription:
      "Please keep Bustly closed. It will reopen automatically.",
    failed: "Update failed",
    failedShort: "Update failed.",
    startFailed: "Failed to start the updater.",
    ready: "Update ready to install",
    downloading: "Downloading update",
    downloadingEllipsis: "Downloading update...",
    available: "Update available",
    pendingVersion: "Version pending",
    versionLabel: "Version {{version}}",
    hideDetails: "Hide details",
    minimize: "Minimize",
    couldNotFinish: "The updater could not finish.",
    tryAgain: "Try again",
    downloadedWithVersion:
      "Version {{version}} has been downloaded. Restart the app to apply the update.",
    downloaded:
      "The update has been downloaded. Restart the app to apply the update.",
    installingShort: "Installing...",
    backgroundNote:
      "You can minimize this window. The update will continue in the background.",
    later: "Later",
    update: "Update",
    restartNow: "Restart now",
    starting: "Starting...",
    downloadNow: "Download now",
  },
  appShell: {
    restore: "Restore",
  },
  cloudAgent: {
    sessionFailed: "Cloud task failed.",
    runtimePreflightFailed: "Cloud runtime preflight failed.",
    workspaceSyncFailed: "Workspace sync failed.",
    finalizing: "Finalizing your workspace...",
    unknownError: "Unknown error",
  },
  agents: {
    labels: {
      overview: "Overview",
      marketing: "Marketing",
      storeOps: "Store Ops",
      customers: "Customers",
      finance: "Finance",
    },
    descriptions: {
      overview: "How can I help you today?",
      marketing: "Highlights wasted spend and recommends where to move budget.",
      storeOps:
        "Flags stockout, inventory backlog, and operational risks early.",
      customers:
        "Identifies churn-risk segments and high-value customer opportunities.",
      finance:
        "Explains how discounts, refunds, and fees affect true net revenue.",
    },
    useCases: {
      dailyPulse: {
        label: "Daily Pulse",
        prompt:
          "Surface the most important business changes of the day across revenue, orders, customers, and risk signals.",
      },
      anomalyDetection: {
        label: "Anomaly Detection",
        prompt:
          "Detect cross-channel anomalies and rank the issues that need attention first, such as refund spikes, stockout risk, or order backlog.",
      },
      executiveHub: {
        label: "Executive Hub",
        prompt:
          "Act as the command center that routes users from a high-level business summary into the right specialized channel for deeper analysis.",
      },
      campaignDiagnosis: {
        label: "Campaign Diagnosis",
        prompt:
          "Identify which campaigns and acquisition channels are driving efficient growth versus wasting budget.",
      },
      budgetGuidance: {
        label: "Budget Guidance",
        prompt:
          "Recommend where to cut spend, where to scale, and which campaigns need immediate optimization.",
      },
      funnelAnalysis: {
        label: "Funnel Analysis",
        prompt:
          "Pinpoint where demand is leaking across the funnel, from traffic to click, conversion, and checkout.",
      },
      fulfillmentRisk: {
        label: "Fulfillment Risk",
        prompt:
          "Highlight overdue, unfulfilled, or exception orders so operators can act before delays impact customer experience.",
      },
      inventoryRisk: {
        label: "Inventory Risk",
        prompt:
          "Detect low-stock and high-velocity SKUs that are likely to stock out in the next 7-14 days.",
      },
      productIssues: {
        label: "Product Issues",
        prompt:
          "Surface products with abnormal sales drops, high return rates, or unusual discount pressure.",
      },
      customerMix: {
        label: "Customer Mix",
        prompt:
          "Show whether growth is driven by healthy repeat demand or by one-time acquisition.",
      },
      retentionOpps: {
        label: "Retention Opps",
        prompt:
          "Identify at-risk customer cohorts and uncover segments that are most worth re-engaging.",
      },
      highValueCustomers: {
        label: "High-Value Customers",
        prompt:
          "Reveal which customer segments contribute the most long-term value, repeat revenue, and profitability.",
      },
      revenueQuality: {
        label: "Revenue Quality",
        prompt:
          "Evaluate business performance beyond top-line sales by focusing on net sales, margin quality, and revenue composition.",
      },
      discountLeakage: {
        label: "Discount Leakage",
        prompt:
          "Quantify how discounts and refunds are eroding revenue and trace the impact back to products, campaigns, or customer groups.",
      },
      healthSignals: {
        label: "Health Signals",
        prompt:
          "Turn financial metrics into clear business signals, such as rising discount dependency or concentrated refund pressure.",
      },
    },
  },
  "userOnboarding": {
    "defaultName": "there",
    "exit": "Back to client",
    "done": "Get Started",
    "logout": "Log out",
    "title": "Set up your first operation.",
    "subtitle": "Bustly prepared one operation based on your workspace role. Review it before you start.",
    "confirmOperation": "Create operation",
    "creatingOperation": "Creating operation...",
    "promptHint": "You can adjust this prompt before creating the operation.",
    "inputPlaceholders": {
      "operationName": "e.g. Store health check",
      "prompt": "Monitor store health, flag issues early, and highlight what needs attention.",
    },
    "questions": {
      "confirmOperation": "What should Bustly handle in this operation?",
    },
    "fields": {
      "operationName": "Operation name",
      "operationPrompt": "What should Bustly handle in this operation?",
    },
    "roles": {
      "owner": "Owner",
      "admin": "Admin",
      "billing": "Billing",
      "member": "Member",
      "customRole": "Custom Role",
      "ecommerceOperations": "Ecommerce Operations",
      "paidMediaChannelGrowth": "Paid Media & Channel Growth",
      "seoContentGrowth": "SEO & Content Growth",
      "socialInfluencerOps": "Social & Influencer Ops",
      "crmLifecycleMarketing": "CRM & Lifecycle Marketing",
      "businessAnalyticsInsights": "Business Analytics & Insights",
      "siteExperienceQa": "Site Experience & QA",
    },
    "defaultPrompts": {
      "operationsManager": "Track workspace operations, key metrics, settings, members, permissions, integrations, and usage so the team knows what needs attention.",
      "billing": "Monitor plan status, payment methods, invoices, Work Units, billing health, and usage questions.",
      "generalist": "Help with general business tasks, summarize context, surface next steps, and keep routine work organized.",
      "ecommerceOps": "Review ecommerce operations, platform health, product operations, orders, inventory, promotion follow-up, and daily recaps.",
      "paidMedia": "Track Google Ads, Meta Ads, channel performance, ROAS, CAC, campaign changes, and likely causes behind performance shifts.",
      "seoContent": "Plan SEO work, blog topics, landing pages, PDP copy, content optimization, and reusable execution checklists.",
      "socialInfluencer": "Monitor Instagram, TikTok, Reddit, creators, KOLs, community signals, sentiment, and content opportunities.",
      "crmLifecycle": "Analyze member segments, EDM, Klaviyo, winback, repeat purchase, lifecycle flows, and automation opportunities.",
      "businessInsights": "Prepare daily and weekly reporting, business analysis, anomaly checks, attribution, channel performance, and user insights.",
      "siteExperience": "Diagnose CRO issues, conversion leaks, Pixel and Shopify connections, site QA, tracking repairs, and product value clarity.",
      "placeholder": "Monitor store health, flag issues early, and highlight what needs attention.",
    },
    "operationNames": {
      "operationsManager": "Operations Manager",
      "billing": "Billing",
      "generalist": "Generalist",
      "ecommerceOps": "Ecommerce Operations",
      "paidMedia": "Paid Media & Channel Growth",
      "seoContent": "SEO & Content Growth",
      "socialInfluencer": "Social & Influencer Ops",
      "crmLifecycle": "CRM & Lifecycle Marketing",
      "businessInsights": "Business Analytics & Insights",
      "siteExperience": "Site Experience & QA",
    },
    "result": {
      "title": "Your operation has been created.",
      "description": "{{operationName}} will appear in your client as your first operation.",
      "badgeMessage": "I'm ready to help with business tasks.",
      "badgeAlt": "Bustly operation badge",
    },
  },
  skillPage: {
    upload: {
      title: "Upload skill",
      dropOrClick: "Drag and drop or click to upload",
      whatHappensNext: "What happens next",
      localSource:
        "You can choose any local file or folder as the skill source.",
      installsShared:
        "Bustly validates it and installs into the shared `~/.bustly/skills` directory.",
      unableToResolvePath:
        "Unable to resolve the dropped path. Please choose a file manually.",
      unavailable: "Skill upload is unavailable in this environment.",
    },
    github: {
      title: "Import from GitHub",
      description: "Import a skill directly from a public GitHub repository.",
      urlLabel: "URL",
      invalidUrl: "Please enter a valid GitHub repository URL.",
      import: "Import",
    },
    uninstall: {
      title: "Delete skill?",
      description: "This will uninstall {{name}} from your workspace.",
      cancel: "Cancel",
      delete: "Delete",
      uninstalling: "Uninstalling...",
    },
    toasts: {
      installSuccessTitle: "Skill Installed",
      installSuccessMessage: "{{name}} has been added to your workspace and assigned to all operations.",
      installFailedTitle: "Install Failed",
      workspaceInstallSuccessTitle: "Skill Installed",
      workspaceInstallSuccessMessage: "{{name}} is now available on this device and assigned to all operations.",
      workspaceInstallFailedTitle: "Install Failed",
      updateSuccessTitle: "Skill Updated",
      updateSuccessMessage: "{{name}} has been updated to the latest version.",
      updateRestartRequiredTitle: "Skill Update Ready",
      updateRestartRequiredMessage: "{{name}} has been prepared. Restart Bustly to finish updating.",
      updateFailedTitle: "Update Failed",
      uninstallSuccessTitle: "Skill Uninstalled",
      uninstallSuccessMessage:
        "{{name}} has been removed from your workspace. You can reinstall it later.",
      uninstallFailedTitle: "Uninstall Failed",
      batchInstallTitle: "Skills Installed",
      batchUpdateTitle: "Skills Updated",
      batchUninstallTitle: "Skills Uninstalled",
      batchWithErrorsTitle: "{{title}} with Errors",
      installedAction: "installed",
      updatedAction: "updated",
      uninstalledAction: "uninstalled",
      batchPartialMessage:
        "{{succeeded}} {{skillLabel}} {{actionLabel}}, {{failed}} failed.",
      batchSuccessMessage:
        "Successfully {{actionLabel}} {{succeeded}} {{skillLabel}}.",
      batchUpdateRestartRequiredMessage:
        "{{prepared}} {{skillLabel}} prepared. Restart Bustly to finish updating.",
      batchUpdateRestartRequiredPartialMessage:
        "{{prepared}} {{skillLabel}} prepared and {{failed}} failed. Restart Bustly to finish updating.",
      skillsSingular: "skill",
      skillsPlural: "skills",
      assignedTitle: "Skills Assigned",
      assignedMessage: "{{count}} {{skillLabel}} assigned to {{operation}}.",
      uploadSuccessTitle: "Skill Uploaded",
      uploadSuccessMessage: "{{name}} has been installed into your workspace and assigned to all operations.",
      uploadFailedTitle: "Upload Failed",
      thisOperation: "this operation",
      workspaceNotReady: "Workspace is not ready yet.",
    },
  },
  "chat": {
    "modelPickerTooltip": "Choose a model for this task.",
    "modelLevels": {
      "auto": {
        "label": "Auto",
        "description": "Automatically selects a model based on the task."
      },
      "standard": {
        "label": "Standard",
        "description": "Fast and efficient for daily tasks."
      },
      "advanced": {
        "label": "Advanced",
        "description": "Best for tasks that need complex reasoning."
      },
      "ultra": {
        "label": "Ultra",
        "description": "Our strongest model for difficult tasks."
      }
    },
    "scrollToBottom": "Scroll to bottom",
    selfEvolution: {
      panel: {
        title: "Turn this task into a reusable skill",
        memoryShort: "Short-term memory",
        memoryLong: "Long-term memory",
        approve: "Create skill",
        review: "Review & save",
        retry: "Retry",
        dismiss: "Dismiss",
        evaluating: "Bustly is reviewing this task for reusable skills",
        applying: "Creating skill",
        approved: "Creating skill",
        drafted: "Needs review",
        applied: "Skill saved",
        failed: "Couldn't create skill",
        reviewFailed: "Couldn't generate reusable skill suggestions. Please retry.",
        actionFailed: "We couldn't update this skill. Try again.",
        preferenceLabel: "Reusable skill suggestions",
        preferenceEnabledTooltip:
          "On: Bustly helps you capture reusable skills after each task.",
        preferenceDisabledTooltip:
          "Off: Bustly won’t suggest reusable skills after tasks.",
      },
      memoryReview: {
        title: "Review Memory",
        memoryType: "Memory type",
        shortTerm: "Short-term memory",
        longTerm: "Long-term memory",
        heading: "Heading",
        body: "Memory",
        save: "Save",
        saving: "Saving",
        saved: "Saved",
        failed: "Save failed. Check the memory and try again.",
      },
      workflowReview: {
        title: "Review and save skill",
        displayName: "Name",
        description: "Description",
        body: "Skill body",
        save: "Save skill",
        saving: "Saving skill",
        saved: "Skill saved",
        failed: "We couldn't save this skill. Review the draft and try again.",
      },
    },
    "taskSidebar": {
      "review": "Review",
      "toggle": "Toggle review panel",
      "show": "Show review panel",
      "hide": "Hide review panel",
      "title": "Review panel",
      "close": "Close review panel",
      "expand": "Expand panel",
      "restore": "Restore panel width",
      "reviewTitle": "Review record",
      "loadingTitle": "Loading review records...",
      "emptyTitle": "No review records yet",
      "emptyDescription": "Key steps and results will appear here after a task runs.",
      "loadError": "Review records could not be loaded from the local ledger.",
      "noActionsDescription": "This task did not perform any actions that need review.",
      "incompleteDescription": "This task ran, but its action details were not fully recorded.",
      "unavailableDescription": "This task's action details could not be loaded.",
      "contentDetails": "Content details"
    },
    "composer": {
      "placeholder": "What should we do next for your business?",
      "followUpPlaceholder": "Ask for follow-up changes",
      "dropFilesHere": "Drop files here",
      "addMenu": "Add files and more",
      "addPhotosAndFiles": "Add photos & files",
      "skills": "Skills",
      "plugins": "Plugins",
      "chromePlugin": "Chrome",
      "chromePluginDescription": "Use signed-in Chrome tabs for this task",
      "addPalette": {
        "add": "Add",
        "filesAndFolders": "Files and folders",
        "useChrome": "Use Chrome",
        "noResults": "No matches"
      },
      "defaultPermission": "Request approval",
      "defaultPermissionDescription": "Ask before write actions that may affect external apps or business data.",
      "fullAccessPermission": "Full access",
      "fullAccessPermissionDescription": "Run actions directly without approval prompts.",
      "stop": "Stop"
    },
    approval: {
      allowOnce: "Yes",
      denyAndAdjust: "No, let Bustly adjust",
      skip: "Skip",
      submit: "Submit",
      submitting: "Submitting...",
      failed: "Approval failed.",
      adjustPlaceholder: "Tell Bustly what to adjust...",
    },
    queue: {
      steer: "Steer",
      queue: "Queue",
      edit: "Edit",
      turnOffQueueing: "Turn off queueing",
      turnOnQueueing: "Turn on queueing",
      removeQueuedMessage: "Remove queued follow-up",
      queuedMessageActions: "Queued follow-up actions",
      defaultQueuedTitle: "Queued follow-up",
      imageCount: "{{count}} image{{plural}}",
      referenceCount: "{{count}} reference{{plural}}",
    },
    timeline: {
      executionFailed: "Execution Failed",
      executionError: "Execution error.",
      connectionError: "Connection error.",
      reason: "Reason:",
      retryNow: "Retry Now",
      editYourMessage: "Edit your request",
      cancel: "Cancel",
      send: "Send",
      sending: "Sending...",
      copy: "Copy",
      copyMessage: "Copy request",
      edit: "Edit",
      editMessage: "Edit request",
      editedMessageEmpty: "Edited request cannot be empty.",
      executionCompleted: "Execution Completed",
      processed: "Processed",
      processedIn: "Processed in {{duration}}",
      openFile: "Open file",
      toolLabel: "Tool",
      detailLabel: "Detail",
      argsLabel: "Args",
      outputLabel: "Output",
      runtimeOffline: "Runtime connection is offline. Reconnect and try again.",
      runtimeTransportUnavailable:
        "Runtime transport unavailable. Reconnect and try again.",
      modelSelectionFailed: "Failed to apply model selection.",
      connectionRecoveredRetry:
        "Retry the request after the connection recovers.",
      rateLimitRetry:
        "The upstream model temporarily rejected the request due to rate limiting. Retry in a moment or switch to a different model tier.",
      refreshCredentials: "Please login again to refresh your credentials.",
      executionStoppedBeforeFinish:
        "Execution stopped before the agent could finish this run. Retry the request or check the runtime connection and model availability.",
      retryUnavailableMissingContext:
        "Retry is unavailable because the original request context is no longer in memory. Submit the request again to continue.",
      retryUnavailableOffline:
        "Retry is unavailable because the runtime connection is offline. Reconnect and try again.",
      retryUnavailableInactivePlan:
        "Retry is unavailable because the current plan is inactive.",
      retryUnavailableRunning:
        "Retry is unavailable while another request is still running.",
      retryUnavailableMissingImageData:
        "Retry is unavailable because the original request included image attachments that are no longer available in memory. Submit the request again and reattach the images to continue.",
    },
    subscription: {
      expiredTitle: "Your plan has expired",
      expiredDescription:
        "Renew to keep chatting with Bustly and continue follow-up tasks.",
      defaultAction: "Upgrade",
    },
  },
};

export default en;
